import React, { useState, useRef, useEffect, useCallback } from 'react';
import { instantCountdown } from '@/lib/eastern';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import Hls from 'hls.js';
import { retentionNotice, filesRemoved } from '@/lib/retentionNotice';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Play, Pause, MessageSquare, Check, CheckCircle, ChevronDown, Columns, ArrowLeft, Send, Upload, Plus, Film, FileText, FileCode, Package, X, Download, Maximize2, Settings2, AlertTriangle, Loader2, Pencil, Trash2, Clock, Circle, HelpCircle } from 'lucide-react';
import { FootageReportForm, FootageReportAdmin, FootageReportClient } from './FootageReport';
import { useConfirm } from './ConfirmDialog';
import SatisfactionSurvey from './SatisfactionSurvey';
import { ApproveAnywayDialog } from './ApproveAnywayDialog';
import { MustHavesPanel, AttestationModal, AttestationReview } from './MustHaves';
import { TranscriptionPanel } from './TranscriptionPanel';
import { soundUploadComplete } from '@/lib/sounds';
import { awaitingRelease } from '@/lib/draftGate';
import { EMPTY_DELIVERY, deliveryComplete, deliveryPayload } from '@/lib/editorTime';
import { refusalOf, asksCheck } from '@/lib/roughEdit';
import { uploadRefusalOf, completionRefused } from '@/lib/uploadRefusal';
import { XML_HOW, XML_ACCEPT, isTimelineXmlName } from '@/lib/timelineXml';
import { listed, totalBytes, sizeWords, removal, removeAsk, removedWords, removeRefusal, freshFilms, cutStatus, replacedPayload,
  CUT_HINT, ZIP_STARTED, zipLine, zipPercent, followZip, split, FILMS_TITLE, FILMS_HINT, REST_TITLE, REST_ZIP } from '@/lib/finalFiles';
import { DeliveryQuestions } from './DeliveryQuestions';
import { offered, originalLabel, speedNote, refreshInMs, resumeFrom, needsSeek, STALL_S } from '@/lib/originalQuality';

function formatTime(s) {
  if (!s && s !== 0) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

// ── "Can't do this" reason guard (2026-07-26) ──
// Larry Tayer cleared all 11 notes on Mashood & Faduma with reason "Done." — the
// shortest string that clears the 5-char minimum. A length check cannot tell
// intent, so we reject reasons that ARE a claim of completion: if the editor did
// the work, the answer is the Done button, not this one.
//
// Deliberately conservative: matches only when the WHOLE reason is a done-phrase,
// so a real explanation that happens to contain the word ("already done in the
// color pass, so this note no longer applies") still passes.
// MIRROR of server.py::_is_done_claim — keep the two lists in sync.
const DONE_CLAIM_PHRASES = new Set([
  'done', 'did it', 'did this', 'already done', 'all done', 'its done', 'it is done',
  'finished', 'complete', 'completed', 'fixed', 'fixed it', 'yes', 'ok', 'okay',
  'tapos', 'tapos na', 'ayos', 'ayos na', 'ginawa', 'ginawa na',
]);
const normalizeReason = (s) => (s || '').toLowerCase().replace(/[^a-z\s]/g, '').replace(/\s+/g, ' ').trim();
const isDoneClaim = (s) => DONE_CLAIM_PHRASES.has(normalizeReason(s));

// Safari detection — cached once
const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

function SafariWarning() {
  if (!isSafari) return null;
  return (
    <div className="flex items-center gap-2 px-3 py-2 mb-2 rounded-lg bg-ug-gold/10 border border-ug-gold/20" data-testid="safari-warning">
      <AlertTriangle className="w-3.5 h-3.5 text-ug-gold shrink-0" />
      <span className="font-sans text-[10px] text-ug-gold/90">Video playback may not work correctly in Safari. For the best experience, use <strong>Chrome</strong>.</span>
    </div>
  );
}

// ─── Switching sources at the same spot (Original <-> Stream) ───
// `resume.current` is {t, level} from the moment a switch is chosen until the new source is at that spot; the player
// reports t meanwhile, so the clock, the timeline and a note posted mid-switch never read the new source's 0. Play or
// pause is the player's own `playing` (playingRef), read when the new source is ready. `positioned`: hls.js was given
// startPosition and puts itself there; the spot is still checked once it can play.
function resumeWhenReady(v, resume, playingRef, positioned = false) {
  if (!resume || !resume.current) return undefined;
  const go = () => {
    const at = resume.current;
    if (!at) return;
    if (needsSeek(v.currentTime, at.t)) v.currentTime = at.t;
    resume.current = null;
    if (playingRef && playingRef.current) v.play().catch(() => {});
  };
  const ev = positioned ? 'canplay' : 'loadedmetadata';
  v.addEventListener(ev, go, { once: true });
  return () => v.removeEventListener(ev, go);
}

// ─── HLS Quality Hook ───
// `original` (ReviewPlayer only): the signed link to the editor's own uploaded file. While it is set hls.js is detached
// and the video plays that file straight from storage by byte ranges (never through our server); cleared, HLS attaches
// again. `load` changes when the same link must be loaded afresh. `resume` and `playingRef`: resumeWhenReady above.
// Called with neither (the drawer's preview), it behaves exactly as it always has.
function useHls(videoRef, hlsUrl, { original = null, load = 0, resume = null, playingRef = null } = {}) {
  const hlsRef = useRef(null);
  const [qualities, setQualities] = useState([]);
  const [currentQuality, setCurrentQuality] = useState(-1); // -1 = auto

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (original) {
      v.src = original;
      return resumeWhenReady(v, resume, playingRef);
    }
    if (!hlsUrl) return;
    if (!Hls.isSupported()) {
      // Safari native HLS
      v.src = hlsUrl;
      return resumeWhenReady(v, resume, playingRef);
    }
    const from = resume ? resume.current : null;
    const hls = new Hls({ startLevel: -1, ...(from && from.t > 0 ? { startPosition: from.t } : {}) });
    hlsRef.current = hls;
    hls.loadSource(hlsUrl);
    hls.attachMedia(v);
    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      const levels = hls.levels.map((l, i) => ({ index: i, height: l.height, width: l.width, bitrate: l.bitrate }));
      setQualities(levels);
      const level = from && from.level >= 0 && from.level < hls.levels.length ? from.level : -1;
      if (level >= 0) hls.currentLevel = level;
      setCurrentQuality(level);
    });
    const stop = resumeWhenReady(v, resume, playingRef, true);
    return () => { if (stop) stop(); hls.destroy(); hlsRef.current = null; };
  }, [hlsUrl, original, load, videoRef, resume, playingRef]);

  const setQuality = useCallback((index) => {
    if (hlsRef.current) {
      hlsRef.current.currentLevel = index; // -1 = auto
      setCurrentQuality(index);
    }
  }, []);

  return { qualities, currentQuality, setQuality, isHls: !!hlsUrl && Hls.isSupported() };
}

// ─── Original: the editor's own file at full quality (lib/originalQuality.js; backend app/original_play.py) ───
// Asked once per version and cut while Stream's copy plays; `fresh()` asks again (a link about to end, or one that
// failed) without touching what is shown, so the caller can mark the spot before the new link is taken.
let canPlayProbe = null;
const canPlay = (type) => {
  try {
    if (!canPlayProbe) canPlayProbe = document.createElement('video');
    return canPlayProbe.canPlayType(type);
  } catch { return ''; }
};
function useOriginal(path) {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    setInfo(null);
    if (!path) return undefined;
    let cancelled = false;
    api.get(path).then(r => { if (!cancelled) setInfo({ ...r.data, gotAt: Date.now() }); }).catch(() => {});
    return () => { cancelled = true; };
  }, [path]);
  const fresh = useCallback(() => (path ? api.get(path).then(r => ({ ...r.data, gotAt: Date.now() })) : Promise.resolve(null)), [path]);
  return { info, setInfo, fresh };
}

// ─── Quality Selector Component ───
// `original` ({label, note}) adds the Original choice under Auto when this browser plays the stored file; `onOriginal`
// says it is the one playing. Choosing it calls onSelect('original').
function QualitySelector({ qualities, currentQuality, onSelect, original = null, onOriginal = false }) {
  const [open, setOpen] = useState(false);
  // Where the open menu sits: above the button, its right edge on the button's (as it always was), but placed on the
  // screen itself so the player's own edges never cut it off and a narrow screen (a phone, where the player is a sliver
  // beside the notes) still shows all of it: moved right as far as it must be to start 8px in from the left.
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [place, setPlace] = useState(null);
  React.useLayoutEffect(() => {
    if (!open || !btnRef.current || !menuRef.current) { setPlace(null); return; }
    const r = btnRef.current.getBoundingClientRect();
    const w = menuRef.current.offsetWidth;
    const vw = window.innerWidth;
    setPlace({ bottom: window.innerHeight - r.top + 4, left: Math.max(8, Math.min(r.right - w, vw - w - 8)) });
  }, [open, original]);
  if ((!qualities || qualities.length === 0) && !original) return null;
  const label = onOriginal ? 'Original' : currentQuality === -1 ? 'Auto' : `${qualities.find(q => q.index === currentQuality)?.height || '?'}p`;
  return (
    <div className="relative">
      <button ref={btnRef} onClick={() => setOpen(!open)} className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-mono text-slate-400 hover:text-ug-gold bg-black/40 border border-ug-border-strong hover:border-gold-leaf/30 transition-colors" data-testid="quality-selector">
        <Settings2 className="w-3 h-3" /> {label}
      </button>
      {open && (
        <div ref={menuRef} style={place ? { bottom: place.bottom, left: place.left } : { left: 0, bottom: 0, visibility: 'hidden' }}
          className={`fixed bg-ug-deepest border border-ug-border-strong rounded-md shadow-xl z-50 ${original ? 'w-[210px]' : 'w-[100px]'} py-1`} data-testid="quality-menu">
          <button onClick={() => { onSelect(-1); setOpen(false); }} data-testid="quality-auto"
            className={`w-full text-left px-3 py-1.5 text-[11px] font-mono ${!onOriginal && currentQuality === -1 ? 'text-ug-gold' : 'text-slate-400'} hover:bg-white/5`}>
            Auto
          </button>
          {original && (
            <button onClick={() => { onSelect('original'); setOpen(false); }} data-testid="quality-original"
              className={`w-full text-left px-3 py-1.5 text-[11px] font-mono ${onOriginal ? 'text-ug-gold' : 'text-slate-400'} hover:bg-white/5`}>
              {original.label}
              <span data-testid="quality-original-note" className="block w-[184px] whitespace-normal font-sans text-[9px] leading-snug text-slate-500">{original.note}</span>
            </button>
          )}
          {qualities.sort((a, b) => b.height - a.height).map(q => (
            <button key={q.index} onClick={() => { onSelect(q.index); setOpen(false); }} data-testid={`quality-level-${q.height}`}
              className={`w-full text-left px-3 py-1.5 text-[11px] font-mono ${!onOriginal && currentQuality === q.index ? 'text-ug-gold' : 'text-slate-400'} hover:bg-white/5`}>
              {q.height}p
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Simple Version Picker (no Radix portal — works in fullscreen layouts) ───
function VersionPicker({ value, versions, onChange, testId, className = '' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open]);
  const current = versions.find(v => v.version_number === value);
  return (
    <div className={`relative ${className}`} ref={ref}>
      <button onClick={() => setOpen(!open)} data-testid={testId}
        className="tap-auto flex items-center gap-2 px-3 h-7 rounded-md bg-ug-surface border border-ug-border text-zinc-100 text-xs hover:border-ug-gold/30 transition-colors min-w-[80px]">
        <span className="flex-1 text-left">V{current?.version_number || value}</span>
        <ChevronDown className={`w-3 h-3 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full mt-1 left-0 bg-ug-raised border border-ug-border-strong rounded-md shadow-xl z-[200] min-w-[100px] py-1">
          {[...versions].sort((a, b) => (b.version_number || 0) - (a.version_number || 0)).map(v => (
            <button key={v.version_number} onClick={() => { onChange(v.version_number); setOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-[11px] font-sans ${v.version_number === value ? 'text-ug-gold font-semibold' : 'text-zinc-300'} hover:bg-white/5`}>
              V{v.version_number}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Single Video Player with timeline markers ───
// `controlRef` (optional): filled with { now, seek } while the player is mounted. `now()` is where the player really is
// (mid-switch, the spot it is going to), read from the video itself; a note is stamped with it rather than with the
// `currentTime` prop, which is only as fresh as the last event that reported it. `seek(t)` moves the video, not just
// the clock. `onPlayPause` (optional): called whenever the video starts or stops playing, whatever started or stopped it.
function ReviewPlayer({ videoUrl, hlsUrl, comments, currentTime, onTimeUpdate, onSeek, onClickTimeline, onPlayPause, controlRef, playing, onTogglePlay, videoRef: externalRef, reviewFileId, reviewFileKey, originalPath }) {
  const internalRef = useRef(null);
  const videoRef = externalRef || internalRef;
  const timelineRef = useRef(null);
  // ORIGINAL (Ryan, 2026-10-25: "Yes, for everyone"): the editor's own uploaded file at full quality, a choice in the
  // quality menu beside Stream's copy (which tops out at 1080p, about 4 Mbps). Auto stays the default. Asked only while
  // Stream's copy is what plays; offered only when this browser plays the file's own codec (lib/originalQuality.js).
  const playingRef = useRef(playing);
  useEffect(() => { playingRef.current = playing; }, [playing]);
  const resumeRef = useRef(null);         // {t, level} from a switch until the new source is at the spot
  const retriedRef = useRef(false);       // a failed Original link is asked for again once
  const lastDurationRef = useRef(0);
  const [onOriginal, setOnOriginalState] = useState(false);
  const onOriginalRef = useRef(false);    // the same, for answers that arrive after the viewer has moved on
  const setOnOriginal = useCallback((on) => { onOriginalRef.current = on; setOnOriginalState(on); }, []);
  const [originalLoad, setOriginalLoad] = useState(0);
  const { info: originalInfo, setInfo: setOriginalInfo, fresh: freshOriginal } = useOriginal(hlsUrl ? originalPath : null);
  const originalOffered = !!hlsUrl && offered(originalInfo, canPlay);
  const originalUrl = onOriginal && originalOffered ? originalInfo.url : null;
  const { qualities, currentQuality, setQuality, isHls } = useHls(videoRef, hlsUrl,
    { original: originalUrl, load: originalLoad, resume: resumeRef, playingRef });

  // Another version or cut: back on Auto, nothing carried over.
  useEffect(() => { setOnOriginal(false); resumeRef.current = null; retriedRef.current = false; }, [originalPath, setOnOriginal]);

  const spotNow = useCallback(() => {
    const v = videoRef.current;
    return resumeRef.current ? resumeRef.current.t : (v ? v.currentTime : 0);
  }, [videoRef]);
  const backToAuto = useCallback((t, words) => {
    if (!onOriginalRef.current) return;   // already off Original: nothing to move
    resumeRef.current = resumeFrom(t, -1);
    setOnOriginal(false);
    if (words) toast(words);
  }, [setOnOriginal]);
  // A fresh link for the same file, loaded at the same spot (a link about to end, or one that failed). An answer that
  // arrives after the viewer left Original is only kept for the next time.
  const takeFresh = useCallback((info, t) => {
    if (!onOriginalRef.current) { if (info) setOriginalInfo(info); return; }
    if (!offered(info, canPlay)) {
      setOriginalInfo(info);
      backToAuto(t, 'The original file is not available now, so the player is back on Auto.');
      return;
    }
    resumeRef.current = resumeFrom(t, -1);
    setOriginalInfo(info);
    setOriginalLoad(n => n + 1);
  }, [backToAuto, setOriginalInfo]);

  const chooseQuality = useCallback((choice) => {
    if (choice === 'original') {
      if (onOriginal || !originalOffered) return;
      retriedRef.current = false;
      if (refreshInMs(originalInfo.expires_in, originalInfo.gotAt, Date.now()) < 30000) {
        // its link is about to end: a fresh one first, then the switch
        freshOriginal().then((info) => {
          if (!offered(info, canPlay)) { setOriginalInfo(info); return; }
          resumeRef.current = resumeFrom(spotNow(), -1);
          setOriginalInfo(info);
          setOnOriginal(true);
        }).catch(() => {});
        return;
      }
      resumeRef.current = resumeFrom(spotNow(), -1);
      setOnOriginal(true);
      return;
    }
    if (onOriginal) {
      resumeRef.current = resumeFrom(spotNow(), choice);
      setOnOriginal(false);
      return;
    }
    setQuality(choice);
  }, [onOriginal, originalOffered, originalInfo, freshOriginal, setOriginalInfo, setOnOriginal, spotNow, setQuality]);

  // While Original plays: its link is asked for again before it ends, and loaded at the same spot.
  useEffect(() => {
    if (!onOriginal || !originalInfo || !originalInfo.gotAt) return undefined;
    const id = setTimeout(() => {
      freshOriginal().then((info) => { retriedRef.current = false; takeFresh(info, spotNow()); })
        .catch(() => {});   // no answer: this link plays on; if it fails, the error below asks again
    }, refreshInMs(originalInfo.expires_in, originalInfo.gotAt, Date.now()));
    return () => clearTimeout(id);
  }, [onOriginal, originalInfo, freshOriginal, takeFresh, spotNow]);

  // An Original link that fails (it ended, storage dropped it): asked for again once and resumed at the same spot;
  // a second failure goes back to Auto at that spot. Safari never calls a link that ended an error: it waits, says the
  // load stalled, and still claims to have data ahead (readyState 3). So STALL_S seconds of playing with the picture
  // held (not mid-switch, not at the end) counts as a failure too, whatever readyState says.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !originalUrl) return undefined;
    const onError = () => {
      const t = spotNow();
      if (retriedRef.current) { backToAuto(t, 'The original file stopped playing here, so the player is back on Auto.'); return; }
      retriedRef.current = true;
      resumeRef.current = resumeFrom(t, -1);
      freshOriginal().then((info) => takeFresh(info, t))
        .catch(() => backToAuto(t, 'The original file stopped playing here, so the player is back on Auto.'));
    };
    let last = v.currentTime;
    let held = 0;
    const watch = setInterval(() => {
      if (!playingRef.current || resumeRef.current || v.ended || v.currentTime !== last) {
        last = v.currentTime; held = 0; return;
      }
      held += 1;
      if (held >= STALL_S) { held = 0; onError(); }
    }, 1000);
    v.addEventListener('error', onError);
    return () => { clearInterval(watch); v.removeEventListener('error', onError); };
  }, [originalUrl, videoRef, freshOriginal, takeFresh, backToAuto, spotNow]);

  useEffect(() => {
    // If NOT using HLS (direct video), set src
    if (!hlsUrl && videoUrl && videoRef.current) {
      videoRef.current.src = videoUrl;
    }
  }, [videoUrl, hlsUrl, videoRef]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    let rafId;
    // mid-switch the new source still reads 0: the spot it is going to is reported instead
    const at = () => (resumeRef.current ? resumeRef.current.t : v.currentTime);
    const tick = () => {
      onTimeUpdate(at());
      rafId = requestAnimationFrame(tick);
    };
    const onPlay = () => { rafId = requestAnimationFrame(tick); if (onPlayPause) onPlayPause(); };
    const onPause = () => { cancelAnimationFrame(rafId); onTimeUpdate(at()); if (onPlayPause) onPlayPause(); };
    // Keep React's `playing` state honest when playback finishes. The browser
    // pauses the element natively at end-of-video; without this the state stays
    // "playing", so the next click reads as a pause on an already-ended video and
    // does nothing (client-reported: couldn't replay without closing the window).
    const onEnded = () => onTogglePlay(false);
    const onSeeked = () => onTimeUpdate(at());
    // A new source (another version or cut) starts at its own spot, with no seek or pause to report it: without this
    // the clock kept the last version's time over the new one's first frame.
    const onLoaded = () => onTimeUpdate(at());
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    v.addEventListener('ended', onEnded);
    v.addEventListener('seeked', onSeeked);
    v.addEventListener('loadedmetadata', onLoaded);
    return () => {
      cancelAnimationFrame(rafId);
      v.removeEventListener('play', onPlay); v.removeEventListener('pause', onPause); v.removeEventListener('ended', onEnded);
      v.removeEventListener('seeked', onSeeked); v.removeEventListener('loadedmetadata', onLoaded);
    };
  }, [onTimeUpdate, onTogglePlay, onPlayPause, videoRef]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (playing) {
      // Replaying after the video ended: restart from the beginning explicitly —
      // deterministic under hls.js/MSE instead of relying on the implicit seek.
      if (v.ended) v.currentTime = 0;
      v.play().catch((err) => {
        // A rejected play() must never leave the UI claiming "playing".
        console.warn('[ReviewPlayer] play() rejected:', err?.name || err);
        onTogglePlay(false);
      });
    } else {
      v.pause();
    }
  }, [playing, onTogglePlay, videoRef]);

  // A seek in the middle of a switch moves the spot the new source starts at, too.
  const handleSeek = (time) => {
    if (resumeRef.current) resumeRef.current = { ...resumeRef.current, t: time };
    if (videoRef.current) { videoRef.current.currentTime = time; onSeek(time); }
  };
  useEffect(() => {
    if (!controlRef) return undefined;
    controlRef.current = { now: spotNow, seek: handleSeek };
    return () => { controlRef.current = null; };
  });

  // The film's length: the new source does not know it until its index is read, so mid-switch it stays the last known.
  const durationNow = videoRef.current?.duration;
  const duration = durationNow > 0 ? durationNow : (resumeRef.current ? lastDurationRef.current : 0);
  useEffect(() => { if (durationNow > 0) lastDurationRef.current = durationNow; }, [durationNow]);

  const handleTimelineClick = (e) => {
    const rect = timelineRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const time = pct * duration;
    handleSeek(time);
    if (onClickTimeline) onClickTimeline(time);
  };

  // Drag scrubbing
  const [dragging, setDragging] = React.useState(false);
  const scrubFromEvent = React.useCallback((e) => {
    if (!timelineRef.current || !videoRef.current) return;
    const rect = timelineRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, ((e.clientX || e.touches?.[0]?.clientX || 0) - rect.left) / rect.width));
    const time = pct * (videoRef.current.duration || lastDurationRef.current || 0);
    if (resumeRef.current) resumeRef.current = { ...resumeRef.current, t: time };
    videoRef.current.currentTime = time;
    onSeek(time);
  }, [onSeek]); // eslint-disable-line react-hooks/exhaustive-deps
  const onMouseDown = (e) => { setDragging(true); scrubFromEvent(e); };
  React.useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => { e.preventDefault(); scrubFromEvent(e); };
    const onUp = () => setDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); window.removeEventListener('touchmove', onMove); window.removeEventListener('touchend', onUp); };
  }, [dragging, scrubFromEvent]);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="rounded-md overflow-hidden bg-black/40 border border-ug-border flex flex-col min-h-0 flex-1">
      {(videoUrl || hlsUrl) ? (
        <div className="w-full bg-black flex-1 min-h-0">
          <video ref={videoRef} src={!hlsUrl ? videoUrl : undefined} className="w-full h-full object-contain" preload="metadata"
            onClick={() => onTogglePlay(!playing)} />
        </div>
      ) : (
        <div className="w-full aspect-video bg-ug-deepest/60 flex items-center justify-center">
          <div className="text-center">
            <Film className="w-10 h-10 text-ug-gold/20 mx-auto mb-2" />
            <p className="font-sans text-[10px] font-semibold text-slate-500 uppercase tracking-wider">No review video uploaded</p>
          </div>
        </div>
      )}
      {/* Timeline bar with markers */}
      <div className="px-3 pt-2 pb-3 bg-ug-raised/60 shrink-0">
        <div ref={timelineRef} onMouseDown={onMouseDown} onTouchStart={onMouseDown} onClick={handleTimelineClick} className="relative h-6 cursor-pointer group select-none" data-testid="video-timeline">
          <div className="absolute top-2.5 left-0 right-0 h-1 bg-white/[0.08] rounded-full overflow-hidden">
            <div className="h-full bg-gold-leaf/60 rounded-full" style={{ width: `${progress}%`, willChange: 'width' }} />
          </div>
          {duration > 0 && comments.map(c => {
            const pos = (c.timecode / duration) * 100;
            return (
              <div key={c.id} onClick={(e) => { e.stopPropagation(); handleSeek(c.timecode); }}
                title={`${formatTime(c.timecode)} — ${c.body.slice(0, 40)}`}
                className={`absolute top-0.5 w-3 h-3 rounded-full border-2 cursor-pointer hover:scale-125 z-10
                  ${c.resolved ? 'bg-oxidized-teal/40 border-oxidized-teal/60' : 'bg-gold-leaf/60 border-gold-leaf'}`}
                style={{ left: `calc(${pos}% - 6px)` }} />
            );
          })}
          <div className="absolute top-1 w-2 h-4 bg-silk-cream rounded-sm shadow-lg" style={{ left: `calc(${progress}% - 4px)`, willChange: 'left' }} />
        </div>
        <div className="flex items-center justify-between mt-1">
          <div className="flex items-center gap-2">
            <button onClick={() => onTogglePlay(!playing)} data-testid="review-play-btn" className="tap-sm w-8 h-8 flex items-center justify-center text-zinc-100 hover:text-ug-gold transition-colors">
              {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </button>
            <span className="font-sans text-[10px] font-semibold text-slate-400" data-testid="review-time">{formatTime(currentTime)} / {formatTime(duration)}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-sans text-[9px] font-semibold text-slate-600">{comments.length} comment{comments.length !== 1 ? 's' : ''}</span>
            {(reviewFileId || reviewFileKey) && (
              <button data-testid="review-video-download-btn" title="Download review video"
                onClick={async () => {
                  try {
                    if (reviewFileKey) {
                      const r = await api.post('/storage/presign-download', { key: reviewFileKey });
                      if (r.data.download_url) { window.location.href = r.data.download_url; return; }
                    }
                  } catch {}
                  if (reviewFileId) window.location.href = `${api.defaults.baseURL}/storage/serve/${reviewFileId}`;
                }}
                className="tap-sm w-7 h-7 flex items-center justify-center text-slate-500 hover:text-ug-gold transition-colors rounded hover:bg-white/5">
                <Download className="w-3.5 h-3.5" />
              </button>
            )}
            {(isHls || originalOffered) && (
              <QualitySelector qualities={qualities} currentQuality={currentQuality} onSelect={chooseQuality} onOriginal={!!originalUrl}
                original={originalOffered ? { label: originalLabel(originalInfo), note: speedNote(originalInfo) } : null} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Drawer compact preview player ───
// Mirrors the fullscreen ReviewPlayer's source handling (SAME useHls hook) so the
// drawer loads instantly when Cloudflare Stream HLS already exists. Falls back to
// the raw R2 file (exactly as before) when HLS isn't ready (just-uploaded versions
// still transcoding) or on Safari (hls.js defers to native) — but with preload
// "none" on the raw fallback so it never triggers the up-front metadata-seek spinner
// that froze long .mov files for ~30s. Compare mode + fullscreen are untouched.
function DrawerPreviewPlayer({ hlsUrl, rawUrl, onDoubleClick }) {
  const videoRef = useRef(null);
  useHls(videoRef, hlsUrl);
  useEffect(() => {
    // Raw fallback only: set src when NOT using HLS (hls.js attaches via MediaSource).
    if (!hlsUrl && rawUrl && videoRef.current) {
      videoRef.current.src = rawUrl;
    }
  }, [hlsUrl, rawUrl]);
  return (
    <video ref={videoRef} src={!hlsUrl ? rawUrl : undefined}
      className="w-full h-full object-contain" preload="none"
      controls controlsList="nodownload nofullscreen"
      onDoubleClick={onDoubleClick} data-testid="drawer-review-video" />
  );
}

// ─── Synced Side-by-Side Compare Player ───
function ComparePlayer({ leftUrl, rightUrl, leftComments, rightComments, activePanel, onSelectPanel, onSeek, onTimeUpdate, seekRef }) {
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const timelineRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const lastUiUpdate = useRef(0);

  // Expose seekBoth to parent via ref
  const seekBoth = (time) => {
    setCurrentTime(time);
    [leftRef, rightRef].forEach(ref => { if (ref.current) ref.current.currentTime = time; });
    if (onSeek) onSeek(time);
  };
  useEffect(() => { if (seekRef) seekRef.current = seekBoth; });

  // Sync both videos — throttled UI updates
  useEffect(() => {
    const v = rightRef.current || leftRef.current;
    if (!v) return;
    let rafId;
    const tick = () => {
      const now = performance.now();
      if (now - lastUiUpdate.current > 250) {
        setCurrentTime(v.currentTime);
        setDuration(v.duration || 0);
        if (onTimeUpdate) onTimeUpdate(v.currentTime);
        lastUiUpdate.current = now;
      }
      rafId = requestAnimationFrame(tick);
    };
    const onPlay = () => { rafId = requestAnimationFrame(tick); };
    const onPause = () => { cancelAnimationFrame(rafId); setCurrentTime(v.currentTime); if (onTimeUpdate) onTimeUpdate(v.currentTime); };
    // Same end-of-video state sync as ReviewPlayer: without it `playing` sticks
    // true after the primary video finishes and the replay click dead-ends.
    const onEnded = () => setPlaying(false);
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    v.addEventListener('ended', onEnded);
    v.addEventListener('seeked', () => { setCurrentTime(v.currentTime); if (onTimeUpdate) onTimeUpdate(v.currentTime); });
    v.addEventListener('loadedmetadata', () => setDuration(v.duration || 0));
    return () => { cancelAnimationFrame(rafId); v.removeEventListener('play', onPlay); v.removeEventListener('pause', onPause); v.removeEventListener('ended', onEnded); };
  }, [onTimeUpdate]);

  const togglePlay = () => {
    const next = !playing;
    setPlaying(next);
    [leftRef, rightRef].forEach(ref => {
      if (ref.current) {
        if (next) {
          // Replay-from-start when that side already finished (same rationale as
          // ReviewPlayer: an explicit seek is deterministic under hls.js/MSE).
          if (ref.current.ended) ref.current.currentTime = 0;
          ref.current.play().catch((err) => {
            // A rejected play() must never leave the UI claiming "playing".
            console.warn('[ComparePlayer] play() rejected:', err?.name || err);
            setPlaying(false);
          });
        } else {
          ref.current.pause();
        }
      }
    });
  };

  const handleTimelineClick = (e) => {
    const rect = timelineRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    seekBoth(pct * duration);
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const activeComments = activePanel === 'left' ? leftComments : rightComments;

  return (
    <div className="space-y-0">
      {/* Side-by-side videos */}
      <div className="grid grid-cols-2 gap-1 rounded-t-md">
        <div className={`relative cursor-pointer rounded-sm ${activePanel === 'left' ? 'border-2 border-gold-leaf' : 'border-2 border-ug-border'}`}
          onClick={() => onSelectPanel('left')}>
          {leftUrl ? <div className="w-full aspect-video bg-black"><video ref={leftRef} src={leftUrl} muted={activePanel !== 'left'} className="w-full h-full object-contain" preload="metadata" /></div> :
            <div className="w-full aspect-video bg-ug-deepest/60 flex items-center justify-center">
              <div className="text-center"><Film className="w-8 h-8 text-ug-gold/20 mx-auto mb-1" /><p className="font-sans text-[9px] font-semibold text-slate-600">No video for this version</p></div>
            </div>}
        </div>
        <div className={`relative cursor-pointer rounded-sm ${activePanel === 'right' ? 'border-2 border-gold-leaf' : 'border-2 border-ug-border'}`}
          onClick={() => onSelectPanel('right')}>
          {rightUrl ? <div className="w-full aspect-video bg-black"><video ref={rightRef} src={rightUrl} muted={activePanel !== 'right'} className="w-full h-full object-contain" preload="metadata" /></div> :
            <div className="w-full aspect-video bg-ug-deepest/60 flex items-center justify-center">
              <div className="text-center"><Film className="w-8 h-8 text-ug-gold/20 mx-auto mb-1" /><p className="font-sans text-[9px] font-semibold text-slate-600">No video for this version</p></div>
            </div>}
        </div>
      </div>
      {/* Shared timeline */}
      <div className="px-3 py-2 bg-ug-raised/60 rounded-b-md border border-ug-border border-t-0">
        <div ref={timelineRef} onClick={handleTimelineClick} className="relative h-6 cursor-pointer">
          <div className="absolute top-2.5 left-0 right-0 h-1 bg-white/[0.08] rounded-full overflow-hidden">
            <div className="h-full bg-gold-leaf/60 rounded-full" style={{ width: `${progress}%`, willChange: 'width' }} />
          </div>
          {duration > 0 && activeComments.map(c => {
            const pos = (c.timecode / duration) * 100;
            return <div key={c.id} onClick={(e) => { e.stopPropagation(); seekBoth(c.timecode); }}
              className={`absolute top-0.5 w-3 h-3 rounded-full border-2 cursor-pointer hover:scale-125 z-10 ${c.resolved ? 'bg-oxidized-teal/40 border-oxidized-teal/60' : 'bg-gold-leaf/60 border-gold-leaf'}`}
              style={{ left: `calc(${pos}% - 6px)` }} />;
          })}
          <div className="absolute top-1 w-2 h-4 bg-silk-cream rounded-sm shadow-lg" style={{ left: `calc(${progress}% - 4px)`, willChange: 'left' }} />
        </div>
        <div className="flex items-center justify-between mt-1">
          <div className="flex items-center gap-2">
            <button onClick={togglePlay} className="tap-sm w-8 h-8 flex items-center justify-center text-zinc-100 hover:text-ug-gold">
              {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </button>
            <span className="font-sans text-[10px] font-semibold text-slate-400">{formatTime(currentTime)} / {formatTime(duration)}</span>
          </div>
          <span className="font-sans text-[9px] font-semibold text-slate-500">
            Showing <span className="text-ug-gold">{activePanel === 'left' ? 'left' : 'right'}</span> comments — click a video to switch
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── Part B admin review surface: N/A dispositions + admin-bypass tombstones on
// the revision cycle that produced the current draft (current_version - 1).
// Mirrors AttestationReview's placement right before approve-and-send. ───
function RevisionDispositionReview({ projectId, cycleVersion, forClient = false }) {
  const [flagged, setFlagged] = useState([]);
  useEffect(() => {
    let gone = false;
    api.get(`/projects/${projectId}/review-comments?version=${cycleVersion}`)
      .then(r => { if (!gone) setFlagged((r.data || []).filter(c => !c.deleted && (c.resolution_type === 'na' || c.admin_upload_bypass))); })
      .catch(() => {});
    return () => { gone = true; };
  }, [projectId, cycleVersion]);
  if (!flagged.length) return null;
  const naCount = flagged.filter(c => c.resolution_type === 'na').length;
  const bypassCount = flagged.filter(c => c.admin_upload_bypass && c.resolution_type !== 'na').length;
  return (
    <div className="rounded-md border border-ug-border bg-ug-surface/40 p-3" data-testid="rev-disposition-review">
      <div className="flex items-center gap-2 mb-2">
        <MessageSquare className="w-3.5 h-3.5 text-ug-gold/70 shrink-0" />
        <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-ug-gold/70 flex-1">V{cycleVersion} Revision Notes — Dispositions</span>
      </div>
      {naCount > 0 && (
        <div className="tap-auto flex items-center gap-2 p-2 rounded-md bg-amber-500/[0.08] border border-amber-500/30 mb-2" data-testid="rev-na-banner">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0" />
          {/* "before approving" is an instruction to the PM at approve-and-send.
              The client sees this panel too and their approval is a different
              action, so they get the neutral pointer instead. */}
          <span className="text-[10px] text-amber-200/90 font-medium">{naCount} note{naCount !== 1 ? 's' : ''} the editor could not do. Review the reason{naCount !== 1 ? 's' : ''} {forClient ? 'below.' : 'before approving.'}</span>
        </div>
      )}
      {bypassCount > 0 && (
        <div className="tap-auto flex items-center gap-2 p-2 rounded-md bg-velvet-burgundy/[0.08] border border-velvet-burgundy/40 mb-2" data-testid="rev-bypass-banner">
          <AlertTriangle className="w-3.5 h-3.5 text-velvet-burgundy shrink-0" />
          <span className="text-[10px] text-zinc-100/85 font-medium">{bypassCount} note{bypassCount !== 1 ? 's' : ''} never addressed — an admin uploaded the new draft while unresolved</span>
        </div>
      )}
      <div>
        {flagged.map((c, i) => (
          <div key={c.id} className={`tap-auto p-2 rounded-md border mb-1.5 last:mb-0 ${c.resolution_type === 'na' ? 'border-amber-500/30 bg-amber-500/[0.05]' : 'border-velvet-burgundy/40 bg-velvet-burgundy/[0.06]'}`} data-testid={`rev-disposition-item-${i}`}>
            <div className="flex items-start gap-2">
              <span className="font-sans text-[9px] font-semibold text-ug-gold bg-gold-leaf/10 px-1.5 py-0.5 rounded shrink-0">{formatTime(c.timecode)}</span>
              <span className="text-[10px] leading-snug break-words flex-1 text-zinc-100/80">{c.body}</span>
              <span className={`font-sans text-[8px] font-semibold uppercase tracking-wider shrink-0 whitespace-nowrap ${c.resolution_type === 'na' ? 'text-amber-400' : 'text-velvet-burgundy'}`}>{c.resolution_type === 'na' ? 'Not done' : 'Unresolved'}</span>
            </div>
            {c.resolution_type === 'na' && c.na_reason && (
              <p className="text-[9px] text-amber-200/70 mt-1 ml-1 italic" data-testid={`rev-disposition-na-reason-${i}`}>"{c.na_reason}"{c.resolved_by_name ? ` — ${c.resolved_by_name}` : ''}</p>
            )}
            {c.admin_upload_bypass && c.resolution_type !== 'na' && (
              <p className="text-[9px] text-velvet-burgundy/90 mt-1 ml-1" data-testid={`rev-disposition-bypass-${i}`}>
                V{c.admin_upload_bypass.uploaded_version} uploaded by {c.admin_upload_bypass.by_name || 'admin'} while this note was unresolved.
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Review Tab Component ───
/** Where a player or a download would have been, once the files are gone under the retention
 *  policy. The words come from lib/retentionNotice.js; this only frames them. Nothing playable
 *  or downloadable is drawn beside it, so nothing can 404 or spin forever. */
function FilesRemovedPanel({ notice, compact = false }) {
  return (
    <div data-testid="files-removed-panel" data-state={notice.state}
      className={`w-full h-full flex items-center justify-center ${compact ? 'p-4' : 'p-8'} bg-ug-deepest/60`}>
      <div className="text-center max-w-md">
        <Film className={`${compact ? 'w-6 h-6' : 'w-10 h-10'} text-ug-status-red/40 mx-auto mb-2`} />
        <p className="font-sans text-[11px] font-semibold text-ug-status-red">{notice.state === 'gone' ? 'Files removed' : 'Files removed, can be brought back'}</p>
        <p className="font-sans text-[10px] text-zinc-400 mt-1 leading-relaxed">{notice.text}</p>
      </div>
    </div>
  );
}

// One archive being prepared (lib/finalFiles.js): what it is doing in words, how far along, a Try Again when it cannot
// go on, and what could not be included. `id` names its test hooks: "deliv" is Download All, "rest" the project files
// and assets alone.
function ZipProgress({ job, id, onRetry }) {
  const percent = zipPercent(job);
  return (
    <div className="p-3 rounded-md bg-ug-surface/60 border border-ug-border space-y-2" data-testid={`${id}-zip-progress`}>
      <div className="flex items-center justify-between">
        <span className="font-sans text-[10px] font-semibold text-zinc-100/70" data-testid={`${id}-zip-status-label`}>{zipLine(job)}</span>
        {job.status === 'error' ? (
          <button onClick={onRetry} data-testid={`${id}-zip-retry`} className="tap-auto font-sans text-[10px] font-semibold text-ug-gold underline">Try Again</button>
        ) : (
          <span className="font-sans text-[10px] font-semibold text-ug-gold">{percent === null ? '' : `${percent}%`}</span>
        )}
      </div>
      {job.status !== 'error' && (
        percent === null ? (
          <div className="w-full h-1.5 bg-white/[0.06] rounded-full overflow-hidden" data-testid={`${id}-zip-indeterminate`}>
            <div className="h-full bg-gold-leaf/50 rounded-full animate-pulse" style={{ width: '40%' }} />
          </div>
        ) : (
          <div className="w-full h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
            <div className="h-full bg-gold-leaf rounded-full transition-all duration-300" style={{ width: `${percent}%` }} />
          </div>
        )
      )}
      {(job.failed_files?.length > 0) && (
        <div className="pt-1" data-testid={`${id}-zip-failures`}>
          <div className="flex items-center gap-3 text-[9px] font-mono">
            <span className="text-emerald-400/90" data-testid={`${id}-zip-included-count`}>✓ {Math.max(0, (job.total || 0) - (job.failed_files?.length || 0))} included</span>
            <span className="text-ug-status-red/90" data-testid={`${id}-zip-unavailable-count`}>✗ {job.failed_files.length} unavailable</span>
          </div>
          {job.status === 'complete' && (
            <details className="mt-1 text-[9px]" data-testid={`${id}-zip-failures-details`}>
              <summary className="cursor-pointer text-slate-500 hover:text-zinc-300">Show unavailable files</summary>
              <ul className="mt-1 space-y-0.5 pl-3 list-disc list-outside text-slate-400">
                {job.failed_files.map((ff, idx) => (
                  <li key={idx} className="break-all">{ff.filename}<span className="text-slate-600 font-mono ml-2">[{ff.scope}]</span></li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

// The same, in the team's narrow panel. `id` names its test hooks as above.
function ZipProgressCompact({ job, id, onRetry }) {
  const percent = zipPercent(job);
  return (
    <div className="p-2 rounded-md bg-ug-surface/60 border border-ug-border space-y-1.5" data-testid={`${id}-zip-progress-compact`}>
      <div className="flex items-center justify-between">
        <span className="font-sans text-[9px] font-semibold text-zinc-100/70" data-testid={`${id}-zip-status-label-compact`}>{zipLine(job)}</span>
        {job.status === 'error' && <button onClick={onRetry} data-testid={`${id}-zip-retry-compact`} className="tap-auto font-sans text-[9px] text-ug-gold underline">Retry</button>}
      </div>
      {job.status !== 'error' && (
        percent === null ? (
          <div className="w-full h-1 bg-white/[0.06] rounded-full overflow-hidden">
            <div className="h-full bg-gold-leaf/60 rounded-full animate-pulse" style={{ width: '40%' }} />
          </div>
        ) : (
          <div className="w-full h-1 bg-white/[0.06] rounded-full overflow-hidden">
            <div className="h-full bg-gold-leaf rounded-full transition-all" style={{ width: `${percent}%` }} />
          </div>
        )
      )}
      {(job.failed_files?.length > 0) && (
        <div className="flex items-center gap-2 text-[8px] font-mono pt-0.5" data-testid={`${id}-zip-counts-compact`}>
          <span className="text-emerald-400/80">✓ {Math.max(0, (job.total || 0) - (job.failed_files?.length || 0))} included</span>
          <span className="text-ug-status-red/80">✗ {job.failed_files.length} unavailable</span>
        </div>
      )}
    </div>
  );
}

export default function VideoReview({ project, onRefresh, readOnly = false, fullscreen = false, initialTime = 0, onFullscreen, onUploading }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const compareSeekRef = useRef(null);
  const [compareVersion, setCompareVersion] = useState(null);
  const [activePanel, setActivePanel] = useState('right');
  const [currentTime, setCurrentTime] = useState(initialTime || 0);
  const [compareTime, setCompareTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [markerTime, setMarkerTime] = useState(null);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingText, setEditingText] = useState('');
  // Part B N/A valve: comment id with the reason input open + its draft reason.
  const [naOpenId, setNaOpenId] = useState(null);
  const [naReason, setNaReason] = useState('');
  const mainVideoRef = React.useRef(null);
  React.useEffect(() => { if (fullscreen && initialTime > 0 && mainVideoRef.current) { mainVideoRef.current.currentTime = initialTime; } }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Editor upload state
  const [showUpload, setShowUpload] = useState(false);
  // ── Must-haves gate (Part A): resolved list + ambient checks + attestation.
  // Fail-open on fetch error — the server backstops (presign + versions/upload)
  // are the real guarantee; the UI gate is the UX layer.
  const [mhGate, setMhGate] = useState(null);
  const [showAttest, setShowAttest] = useState(false);
  // Part B gate: blocking state shown at panel-open when the revision cycle's
  // timestamp notes are unresolved (V2+ only).
  const [showNotesGate, setShowNotesGate] = useState(false);
  const fetchMhGate = useCallback(async () => {
    if (project.project_type === 'photo' || (user.role !== 'editor' && user.role !== 'admin' && user.role !== 'staff')) return;
    try { const r = await api.get(`/projects/${project.id}/must-haves`); setMhGate(r.data); } catch { /* fail-open */ }
  }, [project.id, project.project_type, user.role]);
  useEffect(() => { fetchMhGate(); }, [fetchMhGate]);
  // Single choke point for the upload flow. The dropzones only exist inside
  // the panel, so gating panel-open covers BOTH click-to-choose and drag-drop
  // before any file can be selected (bytes move on file-select). Admin/staff
  // bypass (recovery path — the server writes a visible tombstone).
  const openUploadFlow = () => {
    if (showUpload) { setShowUpload(false); return; }
    if (user.role === 'editor' && mhGate?.required && !mhGate?.confirmed) {
      setShowAttest(true);
      return;
    }
    // Part B (V2+ only — V1 is Part A's territory): unresolved cycle notes block
    // panel entry. Zero-notes cycle = zero friction. Admin/staff bypass (server
    // writes a visible tombstone at versions/upload).
    if (user.role === 'editor' && (project.current_version || 0) >= 1 && project.project_type !== 'photo'
        && (feedbackSummary?.unresolved_count || 0) > 0) {
      setShowNotesGate(true);
      return;
    }
    setShowUpload(true);
  };
  const [reviewVideoFile, setReviewVideoFile] = useState(null);
  const [projectFiles, setProjectFiles] = useState([]);
  // THE TIMELINE XML (backend app/edit_xml.py): every editor's version of a film carries one. Frame Stills reads it,
  // and it is the record of the edit; never shown to the client.
  const [timelineXml, setTimelineXml] = useState(null);
  const [assetFiles, setAssetFiles] = useState([]);
  const [assetsUnchanged, setAssetsUnchanged] = useState(false);
  // ── CUTS upload state ──
  // extraCuts: [{key, file, label}] — companion review videos being uploaded
  // with this version (key doubles as the bg-upload progressKey).
  const [extraCuts, setExtraCuts] = useState([]);
  // User-typed label for the MAIN video, required once extra cuts exist —
  // labels are the cross-version cut identity, so no silent defaults.
  const [primaryLabelInput, setPrimaryLabelInput] = useState('');
  // Click-to-open explainer for the Additional Cuts section.
  const [cutsHelpOpen, setCutsHelpOpen] = useState(false);
  // V2+ carry-forward. primaryUnchanged: re-send the previous version's
  // review_video scalars instead of re-uploading (mirrors "Assets unchanged").
  // unchangedCutIds: previous version's companion file_ids the backend copies
  // forward (versions/upload `unchanged_cut_file_ids`).
  const [primaryUnchanged, setPrimaryUnchanged] = useState(false);
  const [unchangedCutIds, setUnchangedCutIds] = useState([]);
  // An earlier cut this draft's new film replaces: { earlier file_id: the new film's upload key } (lib/finalFiles.js).
  const [replacedBy, setReplacedBy] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [uploadFailures, setUploadFailures] = useState([]); // track failed file names
  const [refused, setRefused] = useState([]); // files the portal would not accept (lib/roughEdit.js), in its words
  // An editor's two answers about this delivery (DeliveryQuestions; the server refuses the upload without them).
  const [delivery, setDelivery] = useState(EMPTY_DELIVERY);
  const [generalNotes, setGeneralNotes] = useState('');
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Controlled, NOT read back out of the DOM by id. This component mounts TWICE
  // for the same project — the drawer's Review tab is forceMount'd (hidden with
  // display:none, still in the document) and full screen renders a second copy
  // portaled to body. Both render this block, so an id lookup returned whichever
  // came first in document order: the hidden drawer one. A client who typed their
  // notes in full screen submitted the OTHER, empty box, and the request went
  // through with nothing in it. Per-instance state means whichever copy you type
  // into is the copy that submits.
  const [revisionNotes, setRevisionNotes] = useState('');
  const [requestingRevisions, setRequestingRevisions] = useState(false);
  const [showSurvey, setShowSurvey] = useState(false);
  const [surveySubmitting, setSurveySubmitting] = useState(false);
  const [showApproveAnyway, setShowApproveAnyway] = useState(false);
  const confirm = useConfirm();
  const [delivZipJob, setDelivZipJob] = useState(null);   // Download All: everything in one archive
  const [restZipJob, setRestZipJob] = useState(null);     // the project files and assets alone (the films go one by one)
  // The run of each being followed (lib/finalFiles.followZip). Another run of the same, or leaving the page, ends it.
  const delivZipRun = useRef(0);
  const restZipRun = useRef(0);
  useEffect(() => { return () => { delivZipRun.current = -1; restZipRun.current = -1; }; }, []);
  const [footageReport, setFootageReport] = useState(null); // { job_id, done, total, status }
  const [revNotesOpen, setRevNotesOpen] = useState(false);
  // Read-only summary: the version the CLIENT left timed feedback on + its count.
  // Drives a prominent editor cue pointed at the reviewed version (not the drawer's
  // current_version default), so timed notes on a prior version aren't missed.
  const [feedbackSummary, setFeedbackSummary] = useState(null);

  const versions = React.useMemo(() => project.versions || [], [project.versions]);
  const currentV = selectedVersion || project.current_version || (versions.length > 0 ? versions[versions.length - 1]?.version_number : 0);
  const compareV = compareVersion || (versions.length > 1 ? versions[0]?.version_number : null);

  // ── CUTS (2026-08): a version may carry companion review videos (vertical /
  // ceremony / reception exports of the SAME edit) as extra type=review_video
  // entries in files[]. The version's review_video_* scalars stay the PRIMARY.
  // selectedCutId is per-instance state ('' = primary) — same double-mount rule
  // as revisionNotes above: drawer and fullscreen copies each keep their own.
  const [selectedCutId, setSelectedCutId] = useState('');
  const curVerObj = versions.find(v => v.version_number === currentV);
  const primaryCutId = curVerObj?.review_video_file_id || '';
  const companionCuts = React.useMemo(() =>
    (curVerObj?.files || []).filter(f => f.type === 'review_video' && f.file_id && f.file_id !== primaryCutId),
    [curVerObj, primaryCutId]);
  const primaryCutLabel = ((curVerObj?.files || []).find(f => f.file_id === primaryCutId)?.label || '').trim() || 'Main';
  // Self-healing: a stale selection (version switched, cut re-uploaded under a
  // new file_id) silently falls back to the primary rather than a dead player.
  const activeCutId = companionCuts.some(c => c.file_id === selectedCutId) ? selectedCutId : '';
  const activeCut = activeCutId ? companionCuts.find(c => c.file_id === activeCutId) : null;
  const cutLabelById = React.useMemo(() => {
    const m = { '': primaryCutLabel };
    companionCuts.forEach((c, i) => { m[c.file_id] = (c.label || '').trim() || `Cut ${i + 2}`; });
    return m;
  }, [companionCuts, primaryCutLabel]);
  // Cross-version cut identity: same file_id when carried forward unchanged; a
  // re-uploaded cut gets a new file_id, so the user-typed LABEL is the identity
  // that survives re-uploads ("Vertical" in V1 is "Vertical" in V3). Falls back
  // to the primary (null) when a version simply doesn't have that cut.
  const resolveCutForVersion = React.useCallback((vNum) => {
    if (!activeCutId) return null; // primary selected
    const ver = versions.find(v => v.version_number === vNum);
    if (!ver) return null;
    const prim = ver.review_video_file_id || '';
    const cands = (ver.files || []).filter(f => f.type === 'review_video' && f.file_id && f.file_id !== prim);
    const wantLabel = (activeCut?.label || '').trim().toLowerCase();
    return cands.find(f => f.file_id === activeCutId)
      || (wantLabel && cands.find(f => (f.label || '').trim().toLowerCase() === wantLabel))
      || null;
  }, [activeCutId, activeCut, versions]);
  // Markers must be cut-scoped — Ceremony and Reception have unrelated
  // timelines, so a 4:32 marker from one is a nonsense timecode on the other.
  // Legacy comments (no deliverable_id) belong to the primary by definition.
  const cutComments = React.useMemo(() =>
    comments.filter(c => (c.deliverable_id || '') === activeCutId),
    [comments, activeCutId]);
  // The rail shows the ACTIVE CUT ONLY — same scoping as the markers, and for
  // the same reason. Cuts are not variants of one video: Part 1 is one portion
  // of the day, Part 2 another, Part 3 another. They share no timeline and no
  // content, so a note on one has nothing to say about another.
  //
  // Until 2026-08-07 the rail merged every cut's notes into a single thread
  // (active cut first, others after, only the others chipped). Watching Part 2
  // you saw Part 1's and Part 3's notes interleaved by timecode under a header
  // counting all of them — read by the client AND the PM as "the notes are
  // showing under the wrong part" (Mila Bat Mitzvah). No data was ever wrong;
  // the rail was just answering a question nobody asked.
  //
  // What the merge bought was the editor's V2+ upload gate, which counts
  // unresolved notes across ALL cuts — a scoped rail can otherwise block an
  // editor on notes they cannot see from where they're standing. That is paid
  // for properly below instead: an unresolved badge on each cut pill and a
  // per-cut breakdown in the gate modal, so the count is visible WHERE the work
  // actually is rather than smeared across every part's thread.
  //
  // Mirrors the server's _cycle_notes_filter (client-authored, non-deleted) so
  // the badge, the gate modal and the upload gate agree on one population.
  const unresolvedByCut = React.useMemo(() => {
    const m = {};
    comments.forEach(c => {
      if (c.author_role !== 'client' || c.deleted || c.resolved) return;
      const k = c.deliverable_id || '';
      m[k] = (m[k] || 0) + 1;
    });
    return m;
  }, [comments]);

  // General-notes (red banner) staleness. `revision_request_notes` is a single
  // OVERWRITTEN field with NO per-version history (audit 2026-06-29), so we never HIDE
  // it (that would lose the only copy). When the note PREDATES the current version it's
  // marked "from a prior version" (muted) instead of shown as current feedback. Fail-safe:
  // if revisions_requested_at or the current version's created_at is missing, treat the
  // note as CURRENT (prominent) — never wrongly mute.
  const hasGeneralNote = !!(project.revision_request_notes && project.revision_request_notes.trim());
  // Mirrors the server's _cycle_notes_filter (client-authored, non-deleted, on the
  // version being reviewed) so the button's enabled state and the endpoint's gate
  // agree. `comments` is already scoped to the current version by the fetch above.
  const clientNoteCount = comments.filter(c => c.author_role === 'client' && !c.deleted).length;
  const canRequestRevisions = !!revisionNotes.trim() || clientNoteCount > 0;
  const curVersionObj = versions.find(v => v.version_number === (project.current_version || 0));
  const generalNoteStale = hasGeneralNote && !!project.revisions_requested_at && !!curVersionObj?.created_at
    && new Date(project.revisions_requested_at) < new Date(curVersionObj.created_at);
  const generalNoteCurrent = hasGeneralNote && !generalNoteStale;
  const generalNoteSourceVersion = (() => {
    if (!generalNoteStale || !project.revisions_requested_at) return null;
    const reqAt = new Date(project.revisions_requested_at);
    const prior = versions.filter(v => v.created_at && new Date(v.created_at) <= reqAt)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return prior.length ? prior[0].version_number : null;
  })();

  useEffect(() => {
    if (project.id && currentV) {
      api.get(`/projects/${project.id}/review-comments?version=${currentV}`).then(r => setComments(r.data)).catch(() => {});
      // Check if review already submitted for this version
      api.get(`/projects/${project.id}/review-submissions`).then(r => {
        const sub = r.data.find(s => s.version_number === currentV);
        if (sub) { setReviewSubmitted(true); setGeneralNotes(sub.general_notes || ''); }
        else { setReviewSubmitted(false); setGeneralNotes(''); }
      }).catch(() => {});
    }
  }, [project.id, currentV]);

  // Editor/admin-only: fetch which version the client left timed feedback on + count
  // (+ unresolved_count — drives the Part B revision-notes upload gate).
  const fetchFeedbackSummary = useCallback(() => {
    if (project.id && (user.role === 'editor' || user.role === 'admin' || user.role === 'staff')) {
      api.get(`/projects/${project.id}/review-comments/summary`).then(r => setFeedbackSummary(r.data)).catch(() => {});
    }
  }, [project.id, user.role]);
  useEffect(() => { fetchFeedbackSummary(); }, [fetchFeedbackSummary, project.current_version]);

  // Fetch footage report
  useEffect(() => {
    if (project.id) {
      api.get(`/projects/${project.id}/footage-report`).then(r => setFootageReport(r.data?.report || null)).catch(() => {});
    }
  }, [project.id]);

  const [compareComments, setCompareComments] = useState([]);
  // Compare mode: the LEFT panel plays compareV's copy of the selected cut, so
  // its markers/rail must be scoped to THAT copy's file_id (a carried-forward
  // cut keeps its id; a re-uploaded one has a new id under the same label).
  // Declared HERE, below the compareComments state — useMemo executes during
  // render, so referencing a later-declared const is a TDZ crash.
  const compareLeftCutId = React.useMemo(() =>
    compareMode ? (resolveCutForVersion(compareV)?.file_id || '') : '',
    [compareMode, compareV, resolveCutForVersion]);
  const compareLeftComments = React.useMemo(() =>
    compareComments.filter(c => (c.deliverable_id || '') === compareLeftCutId),
    [compareComments, compareLeftCutId]);
  useEffect(() => {
    if (compareMode && compareV && project.id) {
      api.get(`/projects/${project.id}/review-comments?version=${compareV}`).then(r => setCompareComments(r.data)).catch(() => {});
    }
  }, [compareMode, compareV, project.id]);

  const handleTimeUpdate = useCallback((t) => setCurrentTime(t), []);
  const handleCompareTimeUpdate = useCallback((t) => setCompareTime(t), []);

  // ── Where a note goes (client-reported 2026-10, Kaylee + Collin: "I made a note at 4:10 while the video was paused
  // at 4:10, then the note said 2:32"). A click on the timeline leaves a marker, and the note used to take the marker
  // for as long as it was set: through play, through pause, until a note was sent. So: click the bar at 2:32, watch to
  // 4:10, pause, write the note, and it went in at 2:32; sending it cleared the marker, which is why the same note sent
  // again went in at 4:10. Now a marker lasts only while the player is still where the click put it: playing, pausing,
  // any other seek, or another version, cut or compare view clears it. With no marker, the time is read from the player
  // itself when the note is sent (mainPlayerRef.now()), never from React's last copy of it.
  const mainPlayerRef = useRef(null);
  const leaveMarker = useCallback(() => setMarkerTime(null), []);
  const handlePlayerSeek = useCallback((t) => { setCurrentTime(t); setMarkerTime(null); }, []);
  useEffect(() => { setMarkerTime(null); }, [currentV, activeCutId, compareMode]);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    // Compare view has no main player: its own clock (ComparePlayer reports on pause and seek) is the time there.
    const playerNow = !compareMode && mainPlayerRef.current ? mainPlayerRef.current.now() : currentTime;
    const tc = markerTime !== null ? markerTime : playerNow;
    try {
      const res = await api.post(`/projects/${project.id}/review-comments`, {
        version_number: currentV, timecode: tc, body: newComment.trim(),
        // CUTS: '' = primary. The note is anchored to the cut being WATCHED —
        // its timecode only means something on that cut's timeline.
        deliverable_id: activeCutId
      });
      setComments(prev => [...prev, res.data].sort((a, b) => a.timecode - b.timecode));
      setNewComment(''); setMarkerTime(null);
      toast.success(`Comment added at ${formatTime(tc)}`);
    } catch { toast.error('Failed'); }
  };

  const handleResolve = async (id) => {
    try {
      await api.put(`/projects/${project.id}/review-comments/${id}/resolve`, { resolution_type: 'checked' });
      setComments(prev => prev.map(c => c.id === id ? { ...c, resolved: true, resolution_type: 'checked' } : c));
      fetchFeedbackSummary();
    } catch { toast.error('Failed'); }
  };

  // Part B "can't do this" valve — resolve with a required short reason (no fee,
  // honest "can't be done"). Counts as resolved for the V2+ upload gate.
  const handleResolveNa = async (id) => {
    const reason = naReason.trim();
    if (reason.length < 5) { toast.error('Please say briefly why this cannot be done'); return; }
    if (isDoneClaim(reason)) {
      toast.error('It sounds like you finished this. Use the Done button instead.', { duration: 7000 });
      return;
    }
    try {
      await api.put(`/projects/${project.id}/review-comments/${id}/resolve`, { resolution_type: 'na', reason });
      setComments(prev => prev.map(c => c.id === id ? { ...c, resolved: true, resolution_type: 'na', na_reason: reason } : c));
      setNaOpenId(null); setNaReason('');
      fetchFeedbackSummary();
      toast.success('Marked as cannot be done. Your project manager will see your reason.');
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : (det?.message || 'Failed'));
    }
  };

  const handleEditComment = async (id) => {
    const text = editingText.trim();
    if (!text) { toast.error('Comment cannot be empty'); return; }
    try {
      const res = await api.put(`/projects/${project.id}/review-comments/${id}`, { body: text });
      setComments(prev => prev.map(c => c.id === id ? { ...c, ...res.data } : c));
      setEditingCommentId(null); setEditingText('');
      toast.success('Comment updated');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to update comment'); }
  };

  const handleDeleteComment = async (id) => {
    if (!window.confirm('Delete this comment? Your editor will see it was removed.')) return;
    try {
      await api.delete(`/projects/${project.id}/review-comments/${id}`);
      // Clients don't see their own deleted comments — drop it locally.
      setComments(prev => prev.filter(c => c.id !== id));
      toast.success('Comment deleted');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to delete comment'); }
  };

  const handleSubmitReview = async () => {
    setSubmitting(true);
    try {
      await api.put(`/projects/${project.id}/submit-review`, {
        version_number: currentV, general_notes: generalNotes, comment_count: comments.length
      });
      setReviewSubmitted(true);
      toast.success('Review submitted — your editor will begin revisions');
      onRefresh();
    } catch { toast.error('Failed to submit review'); }
    finally { setSubmitting(false); }
  };

  const handleTimelineClick = (time) => { setMarkerTime(time); };

  // Per-box upload progress: { video: {loaded, total}, project: {loaded, total}, asset: {loaded, total} }
  const [boxProgress, setBoxProgress] = useState({});

  const formatBytes = (bytes) => {
    if (!bytes || isNaN(bytes)) return '0 KB';
    if (bytes >= 1073741824) return (bytes / 1073741824).toFixed(1) + ' GB';
    if (bytes >= 1048576) return (bytes / 1048576).toFixed(1) + ' MB';
    return (bytes / 1024).toFixed(0) + ' KB';
  };

  // Download All: the archive is made in storage and the browser is sent there for it (no time limit, and a dropped
  // download resumes). A poll that fails is tried again, and a job that is gone is started again once; when it
  // cannot go on, the box says so with a Try Again, where it used to reset without a word.
  // `only`: '' for everything, 'rest' for the project files and assets alone. Each has its own box and its own run.
  const downloadZip = async (only) => {
    const ref = only ? restZipRun : delivZipRun;
    const setJob = only ? setRestZipJob : setDelivZipJob;
    const run = Math.abs(ref.current) + 1;
    ref.current = run;
    const mine = () => ref.current === run;
    const how = await followZip({
      post: (url, body) => api.post(url, body), get: (url) => api.get(url),
      go: (to) => { const a = document.createElement('a'); a.href = `${api.defaults.baseURL}${to}`; document.body.appendChild(a); a.click(); a.remove(); },
      wait: (ms) => new Promise(r => setTimeout(r, ms)),
      onJob: (job) => { if (mine()) setJob(job); },
      projectId: project.id, version: currentV, only, alive: mine,
    });
    if (how === 'started' && mine()) {
      toast.success(ZIP_STARTED, { duration: 12000 });
      setTimeout(() => { if (mine()) setJob(null); }, 4000);
    }
  };
  const handleDownloadAllDeliverables = () => downloadZip('');

  const MULTIPART_THRESHOLD = 100 * 1024 * 1024; // 100MB — use multipart above this
  const CHUNK_SIZE = 50 * 1024 * 1024; // 50MB per part

  const uploadFile = async (file, scope, progressKey) => {
    const version = String((project.current_version || 0) + 1);

    // RESUME BEFORE STARTING OVER. A 2.76 GB master has to land ~53 consecutive
    // parts; before this, one failed part discarded the whole transfer and the
    // next attempt began again at part 1. An editor uploading a 50Mbps export
    // over a slow line could not win that, and stopped trying (2026-09-02).
    // The parts are still in R2, so ask what survived first.
    let resume = null;
    if (file.size > MULTIPART_THRESHOLD) {
      try {
        const r = await api.post('/storage/resume-multipart', {
          filename: file.name, scope, project_id: project.id, version, size: file.size });
        if (r.data?.resumable && (r.data.parts || []).length) resume = r.data;
      } catch { /* resume is an optimisation; a fresh upload is always valid */ }
    }

    const presign = resume
      ? { data: { file_id: resume.file_id, upload_url: 'RESUMED', key: resume.key } }
      : await api.post('/storage/presign-upload', {
          filename: file.name, scope,
          project_id: project.id, version,
          // Recorded at initiate so a later resume can prove it is the SAME
          // export -- a re-render keeps the filename but not the byte count.
          size: file.size,
          content_type: file.type || 'application/octet-stream'
        });
    const isDirectR2 = presign.data.upload_url && presign.data.upload_url !== 'LOCAL';

    // Large files: use multipart upload to R2
    if (isDirectR2 && file.size > MULTIPART_THRESHOLD) {
      const { data: mp } = resume
        ? { data: { upload_id: resume.upload_id } }
        : await api.post('/storage/initiate-multipart', { file_id: presign.data.file_id });
      // Track the in-flight multipart so a failure can ABORT it (no stranded parts)
      // and a retry can restart cleanly. Backend completes from the authoritative
      // server-side part list, so a null browser ETag never breaks completion.
      if (progressKey) activeMultipart.current[progressKey] = { file_id: presign.data.file_id, upload_id: mp.upload_id };
      const totalParts = Math.ceil(file.size / CHUNK_SIZE);
      const parts = [];
      let totalUploaded = 0;

      // Only skip a part whose stored size matches what THIS file would have put
      // there. Size is the cheap integrity check available without re-reading the
      // bytes; a mismatch means the part belongs to a different render and must
      // be re-uploaded rather than trusted.
      const already = new Map();
      if (resume) {
        for (const p of (resume.parts || [])) {
          const i = p.PartNumber - 1;
          if (i < 0 || i >= totalParts) continue;
          const expected = Math.min((i + 1) * CHUNK_SIZE, file.size) - i * CHUNK_SIZE;
          if (p.Size === expected) already.set(p.PartNumber, p);
        }
        for (const [num, p] of already) {
          parts.push({ PartNumber: num, ETag: p.ETag });
          totalUploaded += p.Size;
        }
        if (progressKey) {
          setBoxProgress(prev => ({ ...prev, [progressKey]: { loaded: totalUploaded, total: file.size } }));
        }
        toast.info(`Resuming upload — ${already.size} of ${totalParts} parts already sent`);
      }

      for (let i = 0; i < totalParts; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        const partNum = i + 1;
        if (already.has(partNum)) continue;      // already in R2 — do not re-send

        // Per-part retry: one transient blip retries THIS part (fresh presign)
        // instead of nuking the whole multi-hour transfer.
        let partOk = false, lastErr = null;
        for (let attempt = 1; attempt <= 3 && !partOk; attempt++) {
          try {
            const { data: partData } = await api.post('/storage/presign-part', { file_id: presign.data.file_id, upload_id: mp.upload_id, part_number: partNum });
            const etag = await new Promise((resolve, reject) => {
              const xhr = new XMLHttpRequest();
              xhr.open('PUT', partData.url);
              if (progressKey) activeXhrs.current[progressKey] = xhr;
              xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) {
                  setBoxProgress(prev => ({ ...prev, [progressKey]: { loaded: totalUploaded + e.loaded, total: file.size } }));
                }
              };
              xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.getResponseHeader('ETag'));
                else reject(new Error(`Part ${partNum} failed (${xhr.status})`));
              };
              xhr.onerror = () => reject(new Error(`Part ${partNum} network error`));
              xhr.onabort = () => reject(new Error('__aborted__'));
              xhr.send(chunk);
            });
            parts.push({ PartNumber: partNum, ETag: etag });
            totalUploaded += (end - start);
            partOk = true;
          } catch (e) {
            lastErr = e;
            if (e && e.message === '__aborted__') throw e;   // user aborted — stop
            if (attempt < 3) await new Promise(r => setTimeout(r, 1000 * attempt));
          }
        }
        if (!partOk) throw lastErr || new Error(`Part ${partNum} failed`);
      }

      // Completion is idempotent + retriable server-side; retry a slow/interrupted
      // completion instead of failing (never re-uploads the parts). The server
      // validates completeness against expected_parts/expected_total_bytes — a 409
      // upload_incomplete is NOT transient, so it fails fast with the honest count.
      let completed = false, cErr = null;
      for (let a = 1; a <= 3 && !completed; a++) {
        try {
          await api.post('/storage/complete-multipart', { file_id: presign.data.file_id, upload_id: mp.upload_id, parts, expected_parts: totalParts, expected_total_bytes: file.size });
          completed = true;
        } catch (e) {
          const d = e?.response?.data;
          if (e?.response?.status === 409 && d?.error === 'upload_incomplete') {
            throw new Error(`Upload incomplete — only ${d.uploaded_parts} of ${d.expected_parts} parts reached storage. Please retry the upload.`);
          }
          // The file in storage is not whole (lib/uploadRefusal.js): final, so no second try; its words are shown.
          if (completionRefused(e)) throw e;
          cErr = e; if (a < 3) await new Promise(r => setTimeout(r, 1500 * a));
        }
      }
      if (progressKey) delete activeMultipart.current[progressKey];
      if (!completed) throw cErr || new Error('Completion failed');
    }
    // Small files with presigned URL: single PUT
    else if (isDirectR2) {
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', presign.data.upload_url);
        if (progressKey) activeXhrs.current[progressKey] = xhr;
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setBoxProgress(prev => ({ ...prev, [progressKey]: { loaded: e.loaded, total: e.total } }));
        };
        xhr.onload = () => { delete activeXhrs.current[progressKey]; if (xhr.status >= 200 && xhr.status < 300) resolve(); else reject(new Error(`Upload failed (${xhr.status})`)); };
        xhr.onerror = () => { delete activeXhrs.current[progressKey]; reject(new Error('Network error')); };
        xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
        xhr.send(file);
      });
      await api.post(`/storage/confirm-upload/${presign.data.file_id}`);
    }
    // Fallback: server-mediated upload
    else {
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${api.defaults.baseURL}/storage/upload/${presign.data.file_id}`);
        xhr.withCredentials = true;
        if (progressKey) activeXhrs.current[progressKey] = xhr;
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setBoxProgress(prev => ({ ...prev, [progressKey]: { loaded: e.loaded, total: e.total } }));
        };
        xhr.onload = () => { delete activeXhrs.current[progressKey]; if (xhr.status >= 200 && xhr.status < 300) resolve(); else reject(new Error(`Upload failed (${xhr.status})`)); };
        xhr.onerror = () => { delete activeXhrs.current[progressKey]; reject(new Error('Network error')); };
        const formData = new FormData(); formData.append('file', file);
        xhr.send(formData);
      });
      await api.post(`/storage/confirm-upload/${presign.data.file_id}`);
    }

    setBoxProgress(prev => ({ ...prev, [progressKey]: { loaded: 1, total: 1, done: true } }));
    return presign.data;
  };

  const handleUploadVersion = async () => {
    const assetsOk = assetFiles.length > 0 || (assetsUnchanged && (project.current_version || 0) >= 1);
    // CUTS: on V2+ the primary review video may be carried forward unchanged —
    // the previous version's scalars are re-sent, so the backend guard is
    // untouched and no bytes move.
    const prevVerForCarry = versions.find(v => v.version_number === (project.current_version || 0));
    const primaryCarry = primaryUnchanged && (project.current_version || 0) >= 1 && !!prevVerForCarry?.review_video_file_id;
    const xmlMissing = user.role === 'editor' && !timelineXml;
    if ((!reviewVideoFile && !primaryCarry) || projectFiles.length === 0 || !assetsOk || xmlMissing) {
      const missing = [(!reviewVideoFile && !primaryCarry) && 'Review Video', projectFiles.length === 0 && 'Project File', xmlMissing && 'Timeline XML', !assetsOk && 'Assets'].filter(Boolean);
      toast.error(`Required: ${missing.join(', ')}`); return;
    }
    if (extraCuts.some(c => !(c.label || '').trim())) {
      toast.error('Each additional cut needs a label (e.g. Vertical, Ceremony)'); return;
    }
    const hasCutsSubmit = extraCuts.length > 0 || unchangedCutIds.length > 0;
    if (hasCutsSubmit && !primaryCarry && !primaryLabelInput.trim()) {
      toast.error('Name the main video too (e.g. Horizontal) — labels tell the cuts apart'); return;
    }
    // MANDATORY DISPOSITION mirror of the backend guard: every previous-version
    // cut must be carried forward or replaced by a same-label upload.
    {
      const prevPrim = prevVerForCarry?.review_video_file_id || '';
      const prevCuts = (prevVerForCarry?.files || []).filter(f => f.type === 'review_video' && f.file_id && f.file_id !== prevPrim);
      const fresh = freshFilms({ mainFile: reviewVideoFile, mainUnchanged: primaryUnchanged, mainLabel: primaryLabelInput, extraCuts });
      const undisposed = cutStatus(prevCuts, extraCuts, unchangedCutIds, replacedBy, fresh).filter(st => !st.ok).map(st => st.pc);
      if (undisposed.length > 0) {
        toast.error(`V${project.current_version} had extra cut(s): ${undisposed.map(pc => `"${(pc.label || '').trim() || pc.name || 'cut'}"`).join(', ')}. For each one, check "unchanged" or choose the new file that replaces it.`);
        return;
      }
    }
    if (bgUploading) { toast.error('Files are still uploading — please wait'); return; }
    if (user.role === 'editor' && !deliveryComplete(delivery)) {
      toast.error('Answer the two questions about this delivery first'); return;
    }
    // Bug A gate: require ACTUAL upload success, not just a local file selection.
    // A failed background upload sets boxProgress[key].error and never populates
    // preUploaded; without this, an empty submission would POST -> phantom FDC +
    // auto-payment for undelivered work. (Backend has a matching hard guard.)
    // CUTS note: the primary is checked by its OWN progressKey ('video'), never
    // by find(type==='review_video') — a successfully-uploaded companion must
    // not mask a failed primary.
    {
      const uploaded = Object.values(preUploaded);
      const reviewOk = primaryCarry || !!preUploaded['video']?.file_id;
      const cutsOk = extraCuts.every(c => !!preUploaded[c.key]?.file_id);
      const projOk = uploaded.filter(k => k.type === 'project_file').length >= projectFiles.length;
      const xmlOk = !timelineXml || !!preUploaded['xml']?.file_id;
      const assetOk2 = assetFiles.length === 0 || uploaded.filter(k => k.type === 'asset').length >= assetFiles.length;
      if (!reviewOk || !cutsOk || !projOk || !xmlOk || !assetOk2) {
        const m = 'Some files did not finish uploading. Remove and re-add the failed file(s), then submit again.';
        toast.error(m); setUploadFailures([m]); return;
      }
    }
    setUploading(true);
    setUploadFailures([]); setRefused([]);
    if (onUploading) onUploading(true);
    const reviewTab = document.querySelector('[data-testid="review-tab"]');
    if (reviewTab) reviewTab.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const newV = (project.current_version || 0) + 1;
    try {
      // All files are pre-uploaded — just use them (no re-upload). Built in
      // INTENT order, never Object.entries(preUploaded): that object is keyed
      // by whichever background upload FINISHED first, so a large "Part 2"
      // completing after "Part 3" shipped a files[] of 1,3,2 and the cut pills
      // rendered exactly that (Mila Bat Mitzvah, 2026-08-05). The editor's
      // order is the extraCuts row order — the one they added and typed.
      const uploadedEntries = [];
      if (preUploaded['video']) {
        // The main video carries the user-typed label too (may be '' on a
        // plain single-video upload — no cuts, no label needed).
        uploadedEntries.push({ ...preUploaded['video'], label: primaryLabelInput.trim() });
      }
      for (const cut of extraCuts) {
        const v = preUploaded[cut.key];
        if (v) uploadedEntries.push({ ...v, label: (cut.label || '').trim() });
      }
      // Project files and assets — relative order is never user-visible.
      for (const [k, v] of Object.entries(preUploaded)) {
        if (k !== 'video' && !k.startsWith('cut_')) uploadedEntries.push(v);
      }
      if (uploadedEntries.length === 0 && !primaryCarry) { toast.error('No files uploaded yet'); setUploading(false); return; }
      let files = uploadedEntries;
      let rvKey, rvFid;
      if (primaryCarry) {
        // Re-send the previous version's primary — scalars AND its files[] entry,
        // so the new version's cut list stays complete.
        rvKey = prevVerForCarry.review_video_key || '';
        rvFid = prevVerForCarry.review_video_file_id || '';
        const prevEntry = (prevVerForCarry.files || []).find(f => f.file_id === rvFid)
          || { file_id: rvFid, key: rvKey, name: 'Review Video', type: 'review_video' };
        files = [...files, prevEntry];
      } else {
        const reviewEntry = preUploaded['video'];
        rvKey = reviewEntry?.key || ''; rvFid = reviewEntry?.file_id || '';
      }
      await api.post(`/projects/${project.id}/versions/upload`, {
        files, review_video_key: rvKey, review_video_file_id: rvFid, status_label: 'Deliverable Uploaded',
        unchanged_cut_file_ids: unchangedCutIds,
        replaced_cuts: replacedPayload(
          cutStatus((prevVerForCarry?.files || []).filter(f => f.type === 'review_video' && f.file_id && f.file_id !== (prevVerForCarry?.review_video_file_id || '')),
            extraCuts, unchangedCutIds, replacedBy,
            freshFilms({ mainFile: reviewVideoFile, mainUnchanged: primaryUnchanged, mainLabel: primaryLabelInput, extraCuts })),
          preUploaded),
        ...(user.role === 'editor' ? { delivery_answers: deliveryPayload(delivery) } : {}),
      });
      toast.success(`V${newV} uploaded successfully`);
      soundUploadComplete();
      setShowUpload(false); setReviewVideoFile(null); setProjectFiles([]); setTimelineXml(null); setAssetFiles([]); setUploadProgress(''); setPreUploaded({}); setBgUploadCount(0);
      setExtraCuts([]); setPrimaryUnchanged(false); setUnchangedCutIds([]); setReplacedBy({}); setPrimaryLabelInput(''); setDelivery(EMPTY_DELIVERY); setRefused([]);
      onRefresh();
    } catch (e) {
      const det = e?.response?.data?.detail;
      const msg = typeof e === 'string' ? e : ((typeof det === 'string' ? det : det?.message) || e?.message || 'try again');
      const no = refusalOf(e);
      if (no) {
        // The submit is the guarantee: a file refused there leaves the draft and its list, as one refused on landing does.
        toast.error(no, { duration: 15000 });
        setRefused(prev => prev.includes(no) ? prev : [...prev, no]);
        setPreUploaded(prev => Object.fromEntries(Object.entries(prev).filter(([, v]) => v.file_id !== det.file_id)));
        const gone = Object.values(preUploaded).find(v => v.file_id === det.file_id);
        if (gone && gone.type === 'edit_xml') setTimelineXml(null);
        else if (gone) (gone.type === 'asset' ? setAssetFiles : setProjectFiles)(prev => { const i = prev.findIndex(f => f.name === gone.name); return i < 0 ? prev : prev.filter((_, idx) => idx !== i); });
        return;
      }
      toast.error('Upload failed — ' + msg);
      setUploadFailures(prev => [...prev, msg]);
      if (det?.code === 'revision_notes_unresolved') fetchFeedbackSummary();
    } finally { setUploading(false); if (onUploading) onUploading(false); setUploadProgress(''); }
  };

  // Collapsed video player ref + HLS for Safari compatibility
  const collapsedVideoRef = useRef(null);
  // Silent-disable fix: anchor for the "Complete Footage Analysis" jump-to-form hint.
  const footageFormRef = useRef(null);

  // Track pre-uploaded files (uploaded on drop, before "Create V" clicked)
  const [preUploaded, setPreUploaded] = useState({});
  const [bgUploadCount, setBgUploadCount] = useState(0); // number of background uploads in flight
  const activeXhrs = useRef({}); // { progressKey: xhrObject } for abort
  const activeMultipart = useRef({}); // { progressKey: {file_id, upload_id} } for abort/retry
  const retryFns = useRef({}); // { progressKey: () => bgUploadFile(...) } for a failed-upload retry
  const bgUploading = bgUploadCount > 0;

  const abortUpload = (progressKey) => {
    const xhr = activeXhrs.current[progressKey];
    if (xhr) { xhr.abort(); delete activeXhrs.current[progressKey]; }
    const mp = activeMultipart.current[progressKey];
    if (mp?.upload_id) { api.post('/storage/abort-multipart', mp).catch(() => {}); delete activeMultipart.current[progressKey]; }
    delete retryFns.current[progressKey];
    setPreUploaded(prev => { const n = {...prev}; delete n[progressKey]; return n; });
    setBgUploadCount(c => Math.max(0, c - 1));
    setBoxProgress(prev => { const n = {...prev}; delete n[progressKey]; return n; });
  };

  // Cloudflare Stream refuses files over 30 GB (its per-upload maximum), and a
  // review video that can't stream is delivered anyway — it just condemns the
  // client to scrubbing a raw multi-GB download forever. Catch it BEFORE bytes
  // move, with the fix in the message. Review videos and cuts only: project
  // files (Premiere archives) never touch Stream and may legitimately be huge.
  // Mirrors the presign-upload 413 backstop (server authority; this is the
  // friendly early copy of it).
  const STREAM_MAX_UPLOAD_GB = 30;

  const bgUploadFile = async (file, scope, progressKey) => {
    if ((progressKey === 'video' || progressKey.startsWith('cut_'))
        && file.size > STREAM_MAX_UPLOAD_GB * 1024 ** 3) {
      const msg = `${file.name} is ${(file.size / 1024 ** 3).toFixed(1)} GB — over the ${STREAM_MAX_UPLOAD_GB} GB streaming limit. Re-export as H.264 .mp4, 1080p, 10–15 Mbps VBR (a 3-hour edit lands around 12–18 GB) and upload that instead.`;
      toast.error(msg, { duration: 12000 });
      setBoxProgress(prev => ({ ...prev, [progressKey]: { error: msg } }));
      return;
    }
    // Part A defensive guard — the panel can't render un-gated, but a stale
    // state race must never start bytes moving without the attestation.
    if (user.role === 'editor' && mhGate?.required && !mhGate?.confirmed) {
      toast.error('Confirm the checklist before uploading');
      setShowAttest(true);
      return;
    }
    // Part B defensive guard — unresolved cycle notes must never start NEW bytes
    // moving on a V2+ draft (the presign backstop is the real guarantee).
    if (user.role === 'editor' && (project.current_version || 0) >= 1 && project.project_type !== 'photo'
        && (feedbackSummary?.unresolved_count || 0) > 0) {
      toast.error('Resolve the revision notes before uploading new files');
      setShowNotesGate(true);
      return;
    }
    retryFns.current[progressKey] = () => bgUploadFile(file, scope, progressKey);
    setBoxProgress(prev => { const n = {...prev}; delete n[progressKey]; return n; }); // clear any prior error
    setBgUploadCount(c => c + 1);
    if (onUploading) onUploading(true);
    try {
      const result = await uploadFile(file, scope, progressKey);
      const ftype = (progressKey === 'video' || progressKey.startsWith('cut_')) ? 'review_video' : progressKey === 'xml' ? 'edit_xml' : scope === 'project_asset' ? 'asset' : 'project_file';
      // THE ROUGH EDIT STAYS WITH THE EDITOR (lib/roughEdit.js). The portal reads a project file or an asset as soon
      // as it lands, so a refusal is said here, beside the form, and not at the draft's submit. A refused file leaves
      // its list and never joins the draft. Only a refusal stops a file: if the check cannot be reached the file goes
      // on, and the submit asks again. (The upload still counts as in flight while this runs, so the submit waits.)
      if (asksCheck(user.role, ftype)) {
        let no = null;
        try { await api.post(`/projects/${project.id}/rough-edit-check`, { file_id: result.file_id, name: file.name }); }
        catch (checkErr) { no = refusalOf(checkErr); }
        if (no) {
          (scope === 'project_asset' ? setAssetFiles : setProjectFiles)(prev => prev.filter(f => f !== file));
          setBoxProgress(prev => { const n = {...prev}; delete n[progressKey]; return n; });
          delete retryFns.current[progressKey];
          setRefused(prev => prev.includes(no) ? prev : [...prev, no]);
          toast.error(no, { duration: 15000 });
          return;
        }
      }
      setPreUploaded(prev => ({ ...prev, [progressKey]: { file_id: result.file_id, key: result.key, name: file.name, type: ftype } }));
    } catch (e) {
      // KEEP THE PARTS. This used to abort the multipart on any failure so
      // nothing stranded in R2 -- which is precisely why every retry restarted at
      // part 1. An editor whose 50Mbps master died at part 39 of 53 re-sent 2 GB
      // from scratch each time, five times, and then gave up (2026-09-02).
      //
      // The parts are the only thing that makes the next attempt cheap, so they
      // stay. Genuinely abandoned uploads are reclaimed by the daily sweep, which
      // now expires parts-bearing uploads too rather than only 0-part shells.
      //
      // A USER-INITIATED cancel still aborts (abortUpload) -- that is someone
      // saying they do not want the file, which is different from a line dropping.
      delete activeMultipart.current[progressKey];
      // Part B mid-upload edge (confirmation E): a client note landing mid-flow
      // 409s ONLY this newly-added file — already-uploaded files persist in
      // preUploaded; resolving then hitting Retry passes without re-uploading.
      const _det = e?.response?.data?.detail;
      if (_det?.code === 'revision_notes_unresolved') {
        const n = _det.unresolved_count || 0;
        const midMsg = `A new revision note just arrived — ${n} note${n !== 1 ? 's' : ''} from V${_det.version} need${n === 1 ? 's' : ''} review before new files can be added. Files already uploaded are kept: resolve the note${n !== 1 ? 's' : ''} in the rail, then hit Retry.`;
        toast.error(midMsg, { duration: 9000 });
        setUploadFailures(prev => prev.includes(midMsg) ? prev : [...prev, midMsg]);
        fetchFeedbackSummary();
      }
      // The server refused the file as not whole, or as a video still being exported (lib/uploadRefusal.js): say why,
      // beside the box's Retry, instead of a bare "Upload failed".
      const notWhole = uploadRefusalOf(e);
      if (notWhole) {
        toast.error(notWhole, { duration: 15000 });
        setUploadFailures(prev => prev.includes(notWhole) ? prev : [...prev, notWhole]);
      }
      if (!(e && e.message === '__aborted__')) {
        setBoxProgress(prev => ({ ...prev, [progressKey]: { error: true } }));
      }
    } finally {
      setBgUploadCount(c => { const n = c - 1; if (n <= 0 && onUploading) onUploading(false); return Math.max(0, n); });
    }
  };

  const retryUpload = (progressKey) => { const fn = retryFns.current[progressKey]; if (fn) fn(); };

  // One render for a box's upload state: real error+retry (never a NaN bar), live
  // byte progress, or the uploaded tick.
  const renderUploadState = (key) => {
    const bp = boxProgress[key];
    if (!bp) return null;
    if (bp.error) return (
      <div className="mt-2 flex items-center justify-center gap-2" data-testid={`upload-error-${key}`}>
        <span className="font-sans text-[9px] font-semibold text-ug-status-red">Upload failed</span>
        <button type="button" onClick={(e) => { e.stopPropagation(); retryUpload(key); }}
          data-testid={`upload-retry-${key}`} className="font-sans text-[9px] font-semibold text-ug-gold underline tap-sm">Retry</button>
      </div>
    );
    if (bp.done) return <p className="mt-1 font-sans text-[9px] font-semibold text-oxidized-teal">Uploaded</p>;
    const pct = (bp.total > 0 && !isNaN(bp.loaded)) ? Math.min(100, Math.max(0, (bp.loaded / bp.total) * 100)) : 0;
    return (
      <div className="mt-2" data-testid={`upload-progress-${key}`}>
        <div className="h-1 bg-white/5 rounded-full overflow-hidden"><div className="h-full bg-gold-leaf rounded-full" style={{ width: `${pct}%` }} /></div>
        <p className="font-sans text-[8px] font-medium text-ug-gold/60 mt-0.5">{formatBytes(bp.loaded || 0)} / {formatBytes(bp.total || 0)}</p>
      </div>
    );
  };

  // Drag-and-drop handler factory — starts upload immediately on drop
  const makeDropHandler = (setter, multiple = false) => ({
    onDragOver: (e) => { e.preventDefault(); e.currentTarget.classList.add('border-gold-leaf/50', 'bg-gold-leaf/[0.04]'); },
    onDragLeave: (e) => { e.currentTarget.classList.remove('border-gold-leaf/50', 'bg-gold-leaf/[0.04]'); },
    onDrop: (e) => {
      e.preventDefault(); e.currentTarget.classList.remove('border-gold-leaf/50', 'bg-gold-leaf/[0.04]');
      const files = Array.from(e.dataTransfer.files);
      if (multiple) {
        setter(prev => [...prev, ...files]);
        files.forEach((f, i) => {
          const scope = setter === setAssetFiles ? 'project_asset' : 'project_deliverable';
          const key = setter === setAssetFiles ? `asset_${Date.now()}_${i}` : `project_${Date.now()}_${i}`;
          bgUploadFile(f, scope, key);
        });
      } else {
        setter(files[0] || null);
        if (files[0]) bgUploadFile(files[0], 'project_deliverable', 'video');
      }
    },
  });

  const getVideoUrl = (vNum) => {
    const ver = versions.find(v => v.version_number === vNum);
    if (!ver) return '';
    // Prefer Cloudflare Stream HLS (zero Railway bandwidth)
    if (vNum === currentV && streamInfo.hls_url) return '';  // HLS handled by useHls hook
    // CUTS: a selected companion plays its own file (raw fallback path). Cut
    // identity resolves per-version (file_id, then label) so ANY version plays
    // ITS copy of the selected cut.
    const rc = resolveCutForVersion(vNum);
    if (rc) return `${api.defaults.baseURL}/storage/serve/${rc.file_id}`;
    // Fallback to direct serve only if no HLS available
    if (ver.review_video_file_id) return `${api.defaults.baseURL}/storage/serve/${ver.review_video_file_id}`;
    return ver.review_video_key || '';
  };

  // Raw R2 serve URL — ALWAYS returns the direct file, never defers to HLS.
  // Used by Compare (which stays on raw R2) and as the drawer preview's RAW
  // FALLBACK when Cloudflare Stream HLS isn't ready yet / on Safari. The drawer
  // preview itself now prefers HLS via hls.js (DrawerPreviewPlayer), exactly like
  // the fullscreen ReviewPlayer; it only uses this raw URL (with preload="none")
  // when getHlsUrl() is empty. NOTE: never wire a native <video src=m3u8> in the
  // drawer (can't play in Chrome -> onError -> raw, flickering against the poll);
  // always attach HLS through the useHls/hls.js hook.
  const getRawVideoUrl = (vNum) => {
    const ver = versions.find(v => v.version_number === vNum);
    if (!ver) return '';
    // CUTS: both the drawer preview AND compare panels follow the selected cut —
    // compare pairs each version's copy of the SAME cut (V1 Vertical vs V2
    // Vertical), resolved per-version by file_id then label. A version without
    // that cut falls back to its primary.
    const rc = resolveCutForVersion(vNum);
    if (rc) return `${api.defaults.baseURL}/storage/serve/${rc.file_id}`;
    if (ver.review_video_file_id) return `${api.defaults.baseURL}/storage/serve/${ver.review_video_file_id}`;
    return ver.review_video_key || '';
  };

  // Cloudflare Stream HLS URL — poll until stream is ready
  const [streamInfo, setStreamInfo] = useState({});
  // Additive (Piece 1, 2026-06-10): parallel read of the CF transcode status used
  // ONLY to render the processing/error indicator. Never gates playback (that's
  // streamInfo.hls_url / .ready, untouched). Null = no status -> show nothing.
  const [streamStatus, setStreamStatus] = useState(null);
  const streamReadyRef = useRef(false);
  useEffect(() => { streamReadyRef.current = !!streamInfo.ready; }, [streamInfo.ready]);
  // The two fields the retention helper reads, named so the effect below can depend on exactly
  // them: a whole-object dependency would re-poll on every drawer refresh.
  const { files_recovery_state: recoveryState, files_purged: filesPurged } = project;
  useEffect(() => {
    if (!project.id || !currentV || filesRemoved({ files_recovery_state: recoveryState, files_purged: filesPurged })) { setStreamInfo({}); setStreamStatus(null); return; }
    let cancelled = false;
    setStreamInfo({});      // clear prior project's/version's/cut's HLS so it can't leak across a switch
    setStreamStatus(null);  // reset indicator for the newly-selected version/cut
    const checkStream = () => {
      // CUTS: ?file_id= selects a companion cut's transcode; absent = primary (unchanged behavior).
      api.get(`/projects/${project.id}/stream-info/${currentV}${activeCutId ? `?file_id=${activeCutId}` : ''}`)
        .then(r => { if (!cancelled) { setStreamStatus(r.data); if (r.data.ready) setStreamInfo(r.data); } })
        .catch(() => {});
    };
    checkStream();
    // Poll every 10s until stream is ready (Cloudflare Stream transcoding takes 1-3 min)
    const iv = setInterval(() => {
      if (streamReadyRef.current) { clearInterval(iv); return; }
      checkStream();
    }, 15000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [project.id, currentV, activeCutId, recoveryState, filesPurged]);

  const getHlsUrl = (vNum) => {
    if (vNum === currentV && streamInfo.hls_url) return streamInfo.hls_url;
    return '';
  };

  const isEditor = user.role === 'editor';
  const isAdmin = user.role === 'admin' || user.role === 'staff';
  const isClient = user.role === 'client';
  // Files gone under the retention policy: every player and every download below is replaced by
  // the same panel, for every role. lib/retentionNotice.js decides; null for a live project.
  const removed = retentionNotice(project);

  // Part A attestation modal — rendered by BOTH drawer and fullscreen returns.
  const attestModal = showAttest && mhGate ? (
    <AttestationModal projectId={project.id} items={mhGate.pending_items || []}
      onClose={() => setShowAttest(false)}
      onComplete={(att) => { setMhGate(g => ({ ...(g || {}), attestation: att, confirmed: true, pending_items: [] })); setShowAttest(false); setShowUpload(true); }} />
  ) : null;

  // Part B revision-notes gate — unmissable blocking state at the same panel-open
  // choke point as Part A; routes to the notes rail. Rendered by BOTH returns.
  const notesGateModal = showNotesGate ? (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="revision-notes-gate-modal">
      <div className="w-full max-w-md rounded-xl border border-[#F97066]/40 bg-ug-deepest shadow-2xl p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 data-role="section-title" className="font-sans text-[13px] font-semibold text-zinc-100 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-[#F97066] shrink-0" /> Revision notes need review
          </h3>
          <button type="button" onClick={() => setShowNotesGate(false)} data-testid="revision-notes-gate-close-btn"
            className="tap-auto text-slate-500 hover:text-zinc-200 transition-colors p-0.5"><X className="w-4 h-4" /></button>
        </div>
        <p className="tap-auto text-[12px] text-zinc-100/85 leading-relaxed" data-testid="revision-notes-gate-msg">
          {feedbackSummary?.unresolved_count || 0} revision note{(feedbackSummary?.unresolved_count || 0) !== 1 ? 's' : ''} from V{feedbackSummary?.version} {(feedbackSummary?.unresolved_count || 0) === 1 ? 'is' : 'are'} unchecked.
        </p>
        {/* Per-cut breakdown. The gate counts notes across ALL cuts but the rail
            shows one cut at a time, so naming the parts is what makes the number
            actionable — otherwise an editor clears Part 1, still can't upload,
            and has no idea Part 3 is holding them. */}
        {companionCuts.length > 0 && feedbackSummary?.version === currentV && (
          <ul className="mt-2 space-y-0.5" data-testid="revision-notes-gate-by-cut">
            {[{ file_id: '' }, ...companionCuts]
              .filter(c => (unresolvedByCut[c.file_id || ''] || 0) > 0)
              .map(c => (
                <li key={c.file_id || 'primary'} className="tap-auto text-[11px] text-zinc-100/70 flex items-center gap-1.5">
                  <span className="text-[#F97066] font-semibold">{unresolvedByCut[c.file_id || '']}</span>
                  <span>on {cutLabelById[c.file_id || '']}</span>
                </li>
              ))}
          </ul>
        )}
        <p className="text-[10px] text-slate-400 leading-relaxed mt-1.5">
          Press Done on each note you addressed. If a note is not possible, press "Can't do this" and give a brief reason. Do this for every note before uploading V{(project.current_version || 0) + 1}. {companionCuts.length > 0 && 'Switch parts with the pills above the player to reach each one. '}There is no penalty for an honest answer.
        </p>
        <Button onClick={() => { setShowNotesGate(false); if (!fullscreen && onFullscreen) onFullscreen(); }} data-testid="revision-notes-gate-review-btn"
          className="mt-4 w-full bg-gold-leaf text-obsidian hover:bg-gold-leaf/90 font-sans text-[11px] font-semibold h-9 px-4">
          Review the notes
        </Button>
      </div>
    </div>
  ) : null;

  // ── CUTS: pill switcher, rendered by BOTH returns (drawer + fullscreen) when
  // the viewed version has companion cuts. `.tap-auto` keeps the pills compact
  // (opts out of the global 44px testid min-height). Stays visible in COMPARE
  // mode: compare pairs each version's copy of the SELECTED cut (V1 Vertical vs
  // V2 Vertical) — the resolver in getRawVideoUrl does the per-version match.
  const cutSwitcher = companionCuts.length > 0 ? (
    <div className="flex items-center gap-1 flex-wrap" data-testid="cut-switcher">
      {[{ file_id: '' }, ...companionCuts].map((c) => {
        const id = c.file_id || '';
        const on = activeCutId === id;
        return (
          <button key={id || 'primary'} type="button"
            data-testid={id ? `cut-option-${id}` : 'cut-option-primary'}
            onClick={() => setSelectedCutId(id)}
            className={`tap-auto h-6 px-2.5 rounded-full font-sans text-[9px] font-semibold uppercase tracking-wider border transition-colors inline-flex items-center gap-1.5 ${on ? 'bg-gold-leaf text-obsidian border-gold-leaf' : 'border-ug-border text-slate-400 hover:text-ug-gold hover:border-gold-leaf/30'}`}>
            {cutLabelById[id]}
            {/* Unresolved count, editor/admin only — the rail is scoped to one
                cut now, so this is the ONLY thing standing between an editor
                and being blocked by notes on a part they never opened. */}
            {(isEditor || isAdmin) && (unresolvedByCut[id] || 0) > 0 && (
              <span data-testid={`cut-unresolved-badge-${id || 'primary'}`}
                className={`inline-flex items-center justify-center min-w-[14px] h-[14px] px-1 rounded-full text-[8px] font-bold leading-none ${on ? 'bg-obsidian/25 text-obsidian' : 'bg-[#F97066]/20 text-[#F97066]'}`}>
                {unresolvedByCut[id]}
              </span>
            )}
          </button>
        );
      })}
    </div>
  ) : null;

  // ─── DRAWER MODE: compact preview + prompt ───
  if (!fullscreen) {
    return (
      <div data-testid="review-tab">
        {attestModal}
        {notesGateModal}
        <div className="p-4 sm:p-5 m-0 space-y-4">
          {/* Header — single compact row: version picker (hosts the destructive
              per-version Delete as a red menu item, HIG idiom), Full Screen, Upload.
              tap-auto everywhere: data-testid elements otherwise inflate to the
              global 44px tap-target min-height. */}
          <div className="flex items-center gap-2 pb-3 flex-wrap">
            <Select value={String(currentV)} onValueChange={async (v) => {
              if (v === '__delete__') {
                const ok = await confirm({ title: `Delete V${currentV}?`, message: "This can't be undone.", confirmLabel: 'Delete', destructive: true });
                if (!ok) return;
                try { await api.delete(`/projects/${project.id}/versions/${currentV}`); setSelectedVersion(null); toast.success(`V${currentV} deleted`); onRefresh(); } catch { toast.error('Failed'); }
                return;
              }
              setSelectedVersion(parseInt(v));
            }}>
              <SelectTrigger className="tap-auto bg-ug-surface border-ug-border text-zinc-100 h-7 w-[96px] text-xs" data-testid="version-select">
                <SelectValue placeholder="Version" />
              </SelectTrigger>
              <SelectContent className="bg-ug-raised border-ug-border-strong">
                {[...versions].sort((a, b) => (b.version_number || 0) - (a.version_number || 0)).map(v => (
                  <SelectItem key={v.version_number} value={String(v.version_number)} className="text-zinc-100 text-xs">V{v.version_number}</SelectItem>
                ))}
                {/* Short enough for the fixed 96px trigger: "No versions" showed there cut to "No versior". */}
                {versions.length === 0 && <SelectItem value="0" className="text-slate-500 text-xs">None yet</SelectItem>}
                {(isAdmin || isEditor) && versions.length > 0 && currentV > 0 && !project.client_approved && (
                  <>
                    <SelectSeparator className="bg-ug-border" />
                    <SelectItem value="__delete__" data-testid="delete-version-btn"
                      className="tap-auto text-velvet-burgundy focus:text-velvet-burgundy focus:bg-velvet-burgundy/10 text-xs">
                      Delete V{currentV}
                    </SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
            {cutSwitcher}
            {onFullscreen && (
              <button onClick={onFullscreen} data-testid="expand-review-btn"
                className="tap-auto flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[10px] font-mono uppercase tracking-wider text-slate-400 hover:text-ug-gold hover:bg-white/5 transition-colors border border-ug-border ml-auto">
                <Maximize2 className="w-3.5 h-3.5" /> Full Screen
              </button>
            )}
            {(isEditor || isAdmin) && !readOnly && (
              <Button size="sm" variant="outline" onClick={openUploadFlow} data-testid="upload-version-btn"
                className="tap-auto h-7 font-sans text-[10px] font-semibold border-gold-leaf/20 text-ug-gold/70 hover:bg-gold-leaf/10">
                <Upload className="w-3.5 h-3.5 mr-1" /> Upload V{(project.current_version || 0) + 1}
              </Button>
            )}
          </div>

          {/* Revision Request Notes — collapsible banner for admin/editor. Prominent red
              when CURRENT; muted "from a prior version" when it predates the current
              version (revision_request_notes has no per-version history, so it is marked,
              never hidden — nothing is lost). */}
          {(isAdmin || isEditor) && hasGeneralNote && (
            <button onClick={() => setRevNotesOpen(o => !o)} data-testid="revision-notes-banner"
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-md transition-colors text-left ${generalNoteStale ? 'bg-white/[0.02] border border-ug-border hover:bg-white/[0.04]' : 'bg-[#F97066]/[0.08] border border-[#F97066]/25 hover:bg-[#F97066]/[0.12]'}`}>
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${generalNoteStale ? 'bg-slate-500' : 'bg-[#F97066]'}`} />
              <span className={`font-sans text-[10px] font-semibold uppercase tracking-wider flex-1 ${generalNoteStale ? 'text-slate-500' : 'text-[#F97066]'}`}>
                Client Revision Notes
                {project.revision_paused && (
                  <span className="ml-2 px-1.5 py-0.5 rounded-full border font-sans text-[8px] font-medium normal-case tracking-normal text-oxidized-teal border-oxidized-teal/40 bg-oxidized-teal/10"
                    data-testid="revision-paused-pill" title={`${project.revision_paused.reason}. Paused time does not count; the deadline moves later by it.`}>
                    Clock paused{project.revision_paused.by_name ? ` by ${project.revision_paused.by_name}` : ''}
                  </span>
                )}
                {(() => { const cd = instantCountdown(project.revision_due_at); return cd ? (
                  <span className={`ml-2 px-1.5 py-0.5 rounded-full border font-sans text-[8px] font-medium normal-case tracking-normal ${cd.overdue ? 'text-ug-status-red border-ug-status-red/40 bg-ug-status-red/10' : 'text-ug-gold border-ug-gold/40 bg-ug-gold/10'}`}
                    data-testid="revision-due-pill-header" title="Revised draft due 24 hours after the request. The clock stops when your upload starts.">{cd.text}</span>
                ) : null; })()}
                {generalNoteStale && (
                  <span className="normal-case tracking-normal font-normal text-slate-500" data-testid="revision-notes-prior-label"> · from {generalNoteSourceVersion ? `V${generalNoteSourceVersion}` : 'an earlier version'}{project.revisions_requested_at ? ` · ${new Date(project.revisions_requested_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}</span>
                )}
              </span>
              <ChevronDown className={`w-3 h-3 transition-transform ${revNotesOpen ? 'rotate-180' : ''} ${generalNoteStale ? 'text-slate-500/60' : 'text-[#F97066]/60'}`} />
            </button>
          )}
          {(isAdmin || isEditor) && hasGeneralNote && revNotesOpen && (
            <div className={`px-3 py-2.5 rounded-b-md -mt-2 border border-t-0 ${generalNoteStale ? 'bg-white/[0.01] border-ug-border' : 'bg-[#F97066]/[0.04] border-[#F97066]/15'}`} data-testid="revision-notes-expanded">
              {project.revisions_requested_at && (
                <span className="font-sans text-[8px] text-slate-500 block mb-1">{new Date(project.revisions_requested_at).toLocaleString()}</span>
              )}
              <p className="text-[12px] text-zinc-100/80 whitespace-pre-wrap leading-relaxed">{project.revision_request_notes}</p>
            </div>
          )}

          {/* Prominent timestamped-notes cue (editor/admin) — reflects ONLY the current/
              latest version's client comments. Notes on a superseded version are addressed
              history once a newer version is uploaded, so they correctly recede. Shows even
              when there are NO general notes (the key miss case — no red banner otherwise). */}
          {(isAdmin || isEditor) && feedbackSummary?.count > 0 && (
            <button
              onClick={() => { if (feedbackSummary.version) setSelectedVersion(feedbackSummary.version); if (onFullscreen) onFullscreen(); }}
              data-testid="timestamped-notes-cue"
              className="w-full flex items-center gap-2 px-3 py-2 rounded-md bg-ug-gold/[0.1] border border-ug-gold/40 hover:bg-ug-gold/[0.16] transition-colors text-left">
              <MessageSquare className="w-3.5 h-3.5 text-ug-gold shrink-0" />
              <span className="tap-auto font-sans text-[11px] font-semibold text-ug-gold flex-1 min-w-0 leading-tight" data-testid="timestamped-notes-cue-text">
                {feedbackSummary.count} timestamped note{feedbackSummary.count !== 1 ? 's' : ''} on V{feedbackSummary.version} — open full screen to view
              </span>
              <Maximize2 className="w-3.5 h-3.5 text-ug-gold/60 shrink-0" />
            </button>
          )}

          {/* Muted "awaiting client feedback" — shows when the current version has NO
              feedback that counts as CURRENT: no timestamp comments AND no CURRENT general
              note. A STALE general note (predates current version) does NOT suppress this —
              it's prior feedback, not current. Because the prominent red banner only renders
              for a CURRENT note (generalNoteCurrent), this line can never show alongside the
              red banner; it CAN coexist with a MUTED "from a prior version" note, which reads
              coherently ("here's what they said before; now awaiting their V{n} response"). */}
          {(isAdmin || isEditor) && feedbackSummary && feedbackSummary.count === 0 && feedbackSummary.version > 0 && !generalNoteCurrent && (
            <div data-testid="awaiting-feedback-line" className="flex items-center gap-2 px-3 py-2 rounded-md bg-white/[0.02] border border-ug-border">
              <Clock className="w-3.5 h-3.5 text-slate-500 shrink-0" />
              <span className="font-sans text-[10px] text-slate-500">Awaiting client feedback on V{feedbackSummary.version}</span>
            </div>
          )}

          {/* Non-English project — transcription instructions with the shared
              team-tool login. Editor role ONLY (not admin/staff);
              English/default projects render nothing. */}
          {isEditor && project.wedding_language && project.wedding_language !== 'English' && (
            <div className="pt-4">
              <TranscriptionPanel language={project.wedding_language} />
            </div>
          )}

          {/* Ambient first-draft checklist (Part A) — the editor works through it
              here, beside the upload button: the must-haves (with the fee warning),
              then "I read this client's notes again", which is always there. */}
          {isEditor && !readOnly && mhGate?.required && !mhGate?.confirmed && (
            <MustHavesPanel projectId={project.id} gate={{ ...mhGate, items: mhGate.pending_items || [] }} onChanged={fetchMhGate} />
          )}

          {/* Upload panel (editor/admin only) */}
          {showUpload && (isEditor || isAdmin) && renderUploadPanel()}

          {/* Video preview */}
          <SafariWarning />
          <div className="rounded-md overflow-hidden bg-black/40 border border-ug-border">
            <div className="w-full aspect-video bg-black relative" style={{ maxHeight: '280px' }}>
              {removed ? (
                <FilesRemovedPanel notice={removed} compact />
              ) : (getHlsUrl(currentV) || getRawVideoUrl(currentV)) ? (
                <DrawerPreviewPlayer
                  hlsUrl={getHlsUrl(currentV) || undefined}
                  rawUrl={getRawVideoUrl(currentV)}
                  onDoubleClick={() => { if (onFullscreen) onFullscreen(); }}
                />
              ) : versions.find(v => v.version_number === currentV)?.review_video_file_id ? (
                <div className="w-full h-full flex items-center justify-center bg-ug-deepest/60">
                  <div className="text-center">
                    <div className="w-6 h-6 rounded-full border-2 border-ug-gold/30 border-t-ug-gold animate-spin mx-auto mb-2" />
                    <p className="font-sans text-[10px] text-slate-500">Processing video...</p>
                  </div>
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-ug-deepest/60">
                  <Film className="w-10 h-10 text-ug-gold/20" />
                </div>
              )}
            </div>
          </div>
          {streamStatus?.pruned && (
            <p data-testid="pruned-playback-note" className="tap-auto font-sans text-[11px] text-zinc-500 mt-1.5 px-0.5">
              {/* a .mov or HEVC master (pruned from 60 days after approval, any format) may not play in every browser */}
              {streamStatus.may_not_play ? 'If this film does not play here, download it to watch.' : 'Loading may take a moment for completed projects.'}
            </p>
          )}
          {/* RAW-PLAYBACK marker (2026-08-05) — non-client roles only. Exists
              because the quota incident was invisible from the inside: state
              "none" (no Stream registration at all) deliberately shows the
              CLIENT nothing, so when the account filled up and every new upload
              413'd, nothing on any internal screen changed. This surfaces it to
              staff without alarming clients. NOTE: legacy pre-Stream videos also
              carry state "none", so this marker on an old project is truthful,
              not a bug. Deliberately-pruned videos say "pruned", not this. */}
          {user.role !== 'client' && streamInfo && streamInfo.state === 'none' && !streamInfo.ready && (
            <p data-testid="raw-playback-marker" className="tap-auto font-sans text-[10px] text-zinc-500 mt-1.5 px-0.5 flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3 shrink-0 text-zinc-500" />
              No Stream copy — clients get the raw file (slow to load and scrub). Re-push it from Settings &rarr; Cloudflare Stream.
            </p>
          )}

          {/* Deliverable downloads for current version — hidden from clients until approved */}
          {user.role !== 'client' && !removed && (() => {
            // The version's own films, then every version's project files and assets, once each (lib/finalFiles.js).
            const vFiles = listed(versions, currentV);
            if (vFiles.length === 0) return null;
            const getDownloadUrl = async (f) => {
              if (f.key) {
                try {
                  const r = await api.post('/storage/presign-download', { key: f.key });
                  return r.data.download_url;
                } catch (err) {
                  if (err.response?.status === 410) {
                    toast.error(err.response.data?.message || 'This file is no longer available in storage. Please contact support.');
                    return '';
                  }
                }
              }
              if (f.file_id) return `${api.defaults.baseURL}/storage/serve/${f.file_id}`;
              return '';
            };
            return (
              <div className="space-y-1.5 mt-2">
                <span className="font-sans text-[8px] font-medium uppercase tracking-wider text-slate-500">V{currentV} Deliverables</span>
                {vFiles.length > 1 && (
                  <div className="mb-1">
                    {delivZipJob ? (
                      <ZipProgressCompact job={delivZipJob} id="deliv" onRetry={() => { setDelivZipJob(null); downloadZip(''); }} />
                    ) : (
                      <button onClick={handleDownloadAllDeliverables} data-testid="download-all-deliverables-btn-compact" className="w-full flex items-center justify-center gap-1.5 p-2 rounded-md bg-gold-leaf/10 border border-gold-leaf/25 hover:bg-gold-leaf/20 transition-colors">
                        <Download className="w-3 h-3 text-ug-gold" />
                        <span className="font-sans text-[9px] font-medium text-ug-gold uppercase tracking-wider">Download All as ZIP{totalBytes(vFiles) > 0 ? ` (${sizeWords(totalBytes(vFiles))})` : ''}</span>
                      </button>
                    )}
                  </div>
                )}
                {/* The same two groups the client sees (lib/finalFiles.split; Ryan, 2026-10-13): the films, each
                    downloaded on its own, then the project files and assets, with one zip of their own. */}
                {(() => {
                  const { films, rest, restZip } = split(vFiles);
                  const row = (f) => {
                  const i = vFiles.indexOf(f);            // its place in the whole list, as before the groups
                  const icon = f.type === 'review_video' ? Film : f.type === 'project_file' ? FileText : f.type === 'edit_xml' ? FileCode : Package;
                  const Icon = icon;
                  const label = f.type === 'review_video' ? 'Review Video' : f.type === 'project_file' ? 'Project File' : f.type === 'edit_xml' ? 'Timeline XML' : 'Asset';
                  const size = sizeWords(f.size);
                  return (
                    <div key={f.file_id || i} className="flex items-center gap-1.5 py-1.5 px-2 rounded bg-ug-surface/40 border border-ug-border-subtle hover:bg-ug-surface/60 transition-colors min-w-0">
                      <Icon className="w-3 h-3 text-oxidized-teal shrink-0" />
                      <button onClick={async () => { const u = await getDownloadUrl(f); if (u) window.location.href = u; }} className="text-[10px] text-zinc-100/80 flex-1 truncate min-w-0 hover:text-ug-gold text-left">{f.name}</button>
                      {size && <span className="text-[7px] text-zinc-600 font-mono shrink-0">{size}</span>}
                      <span className="text-[7px] text-zinc-500 font-mono shrink-0 px-1 py-0.5 rounded bg-white/[0.03]">{label}</span>
                      <button onClick={async () => { const u = await getDownloadUrl(f); if (u) window.location.href = u; }} className="shrink-0"><Download className="w-2.5 h-2.5 text-zinc-500 hover:text-ug-gold" /></button>
                      {/* Off ITS OWN version's list, named by what it is (lib/finalFiles.removal): the place in this
                          merged list is not its place in storage, and once took a different file. */}
                      <button onClick={async (e) => { e.preventDefault(); const ok = await confirm({ title: 'Take this file off the list?', message: removeAsk(f), confirmLabel: 'Take it off', destructive: true }); if (!ok) return; try { const { url, params } = removal(project.id, f, i); const r = await api.delete(url, { params }); toast.success(removedWords(f, r.data)); onRefresh(); } catch (err) { toast.error(removeRefusal(err)); } }}
                        data-testid={`remove-version-file-${f.file_id || i}`}
                        className="text-zinc-600 hover:text-ug-status-red transition-colors shrink-0" title={`Take off V${f._v}'s list`}>
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  );
                  };
                  const title = "font-sans text-[8px] font-medium uppercase tracking-wider text-slate-600 pt-0.5";
                  return (
                    <>
                      {films.length > 0 && <p className={title} data-testid="team-films-title">{FILMS_TITLE} ({films.length})</p>}
                      {films.map(f => row(f))}
                      {rest.length > 0 && <p className={title} data-testid="team-rest-title">{REST_TITLE} ({rest.length})</p>}
                      {restZip && (restZipJob ? (
                        <ZipProgressCompact job={restZipJob} id="rest" onRetry={() => { setRestZipJob(null); downloadZip('rest'); }} />
                      ) : (
                        <button onClick={() => downloadZip('rest')} data-testid="download-rest-zip-btn-compact"
                          className="w-full flex items-center justify-center gap-1.5 p-1.5 rounded-md border border-gold-leaf/25 hover:bg-gold-leaf/10 transition-colors">
                          <Download className="w-3 h-3 text-ug-gold" />
                          <span className="font-sans text-[9px] font-medium text-ug-gold uppercase tracking-wider">{REST_ZIP}{totalBytes(rest) > 0 ? ` (${sizeWords(totalBytes(rest))})` : ''}</span>
                        </button>
                      ))}
                      {rest.map(f => row(f))}
                    </>
                  );
                })()}
                {/* Add file to this version */}
                <label className="flex items-center gap-1.5 py-1 px-2 rounded border border-dashed border-ug-border hover:border-gold-leaf/30 hover:bg-gold-leaf/[0.02] transition-colors cursor-pointer">
                  <Plus className="w-3 h-3 text-zinc-600" />
                  <span className="text-[9px] text-zinc-600 font-mono">Add file to V{currentV}</span>
                  <input type="file" className="hidden" onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      const presign = await api.post('/storage/presign-upload', { filename: file.name, scope: 'project_deliverable', project_id: project.id, version: String(currentV), content_type: file.type || 'application/octet-stream' });
                      if (presign.data.upload_url && presign.data.upload_url !== 'LOCAL') {
                        // fetch() never throws on 4xx/5xx — check res.ok so a failed PUT
                        // isn't confirmed as an empty "completed" deliverable.
                        const putRes = await fetch(presign.data.upload_url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type || 'application/octet-stream' } });
                        if (!putRes.ok) throw new Error(`Storage upload failed (${putRes.status})`);
                      } else {
                        const formData = new FormData(); formData.append('file', file);
                        await api.post(`/storage/upload/${presign.data.file_id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                      }
                      await api.post(`/storage/confirm-upload/${presign.data.file_id}`);
                      await api.post(`/projects/${project.id}/versions/${currentV}/files`, { name: file.name, key: presign.data.key, type: /\.(xml|fcpxml)$/i.test(file.name) ? 'edit_xml' : 'asset', file_id: presign.data.file_id });
                      toast.success(`Added ${file.name}`);
                      onRefresh();
                    } catch (addErr) {
                      const said = refusalOf(addErr) || uploadRefusalOf(addErr);
                      toast.error(said || 'Upload failed', said ? { duration: 15000 } : undefined);
                    }
                    e.target.value = '';
                  }} />
                </label>
              </div>
            );
          })()}

          {/* Prompt to enter fullscreen */}
          <div className="text-center py-3 space-y-2">
            {/* Scoped to the selected part, like the rail. This line sits
                directly under the cut pills, so a version-wide total reads as
                "notes on the part you're looking at" — the same confusion the
                rail was causing. */}
            <p className="text-[11px] text-slate-400" data-testid="drawer-comment-count">
              {cutComments.length > 0
                ? `${cutComments.length} comment${cutComments.length > 1 ? 's' : ''} on ${companionCuts.length > 0 ? cutLabelById[activeCutId] : `V${currentV}`}`
                : `No comments yet${companionCuts.length > 0 ? ` on ${cutLabelById[activeCutId]}` : ''}`}
            </p>
            <div className="flex items-center justify-center p-3 rounded-xl bg-ug-gold/8 border border-ug-gold/20 mt-2" data-testid="fullscreen-review-hint">
              <p className="font-sans text-[10px] font-semibold text-ug-gold uppercase tracking-wider text-center">
                {isEditor
                  ? 'Enter full screen to review, view timestamped notes, and compare versions'
                  : 'Enter full screen to review, leave timestamped notes, and compare versions'}
              </p>
            </div>
          </div>

          {/* Footage Report — admin view */}
          {isAdmin && footageReport && (
            <FootageReportAdmin report={footageReport} projectId={project.id} onUpdate={() => {
              api.get(`/projects/${project.id}/footage-report`).then(r => setFootageReport(r.data?.report || null)).catch(() => {});
            }} />
          )}

          {/* Footage Report — client view (only after PM approval) */}
          {user.role === 'client' && footageReport && (
            <FootageReportClient report={footageReport} />
          )}

          {/* Client: read-back of their OWN submitted revision request (display-only).
              Persists whenever revision_request_notes exists — i.e. BOTH while
              revisions_requested=true AND after a new version lands (flag resets but
              notes remain), so the client can always see what they previously asked
              for and compare it against the delivered version. Labeled as a RECORD of
              what they sent ("Your revision request · sent {date}"), never a prompt to
              resubmit. Reads the existing project payload field — no write/storage
              change, and the staff red dropdown above stays admin/editor-only. */}
          {user.role === 'client' && project.revision_request_notes && project.revision_request_notes.trim() && (
            <div
              data-testid="client-revision-request-readback"
              className="p-3 rounded-md bg-[#F97066]/[0.06] border border-[#F97066]/25">
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <span className="w-1.5 h-1.5 rounded-full bg-[#F97066] shrink-0" />
                <span className="font-sans text-[10px] font-semibold uppercase tracking-wider text-[#F97066]">
                  Your revision request
                </span>
                {project.revisions_requested_at && (
                  <span className="font-sans text-[8px] text-slate-500 normal-case tracking-normal">
                    · sent {new Date(project.revisions_requested_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                )}
              </div>
              <p className="text-[12px] text-zinc-100/80 whitespace-pre-wrap leading-relaxed">{project.revision_request_notes}</p>
            </div>
          )}

          {/* Client: Request Revisions button */}
          {/* W1 (2026-05-27): drop draft_approved check — server already strips
              unreleased versions for clients (legacy: full strip when draft_approved=false;
              per-version: filter to released_to_client=true). So versions.length > 0
              ⇒ at least one released version is viewable. */}
          {user.role === 'client' && versions.length > 0 && (
            project.revisions_requested ? (
              <div className="space-y-2">
                <div className="p-3 rounded-md bg-ug-surface/60 border border-ug-border text-center">
                  <span className="font-sans text-[11px] font-medium text-slate-400">Revisions Requested</span>
                  <Check className="w-4 h-4 text-slate-400 inline ml-1" />
                </div>
                {/* Secondary, understated "approve anyway" path for a client who
                    changed their mind. Deliberately quiet so a client still
                    waiting on their revision isn't nudged to abandon it. Confirm
                    dialog -> existing approve flow (survey -> client-approve). */}
                {!project.client_approved && (
                  showSurvey ? (
                    <div className="p-4 rounded-xl bg-ug-raised border border-ug-border">
                      <SatisfactionSurvey
                        submitting={surveySubmitting}
                        onCancel={() => setShowSurvey(false)}
                        onSubmit={async (satisfaction) => {
                          setSurveySubmitting(true);
                          try {
                            await api.put(`/projects/${project.id}/client-approve`, { satisfaction });
                            toast.success('Project approved! Your files are now available.');
                            onRefresh();
                          } catch { toast.error('Failed'); }
                          finally { setSurveySubmitting(false); }
                        }}
                      />
                    </div>
                  ) : (
                    <button onClick={() => setShowApproveAnyway(true)} data-testid="approve-anyway-btn"
                      className="w-full text-center text-[10px] text-slate-500 hover:text-slate-300 underline underline-offset-2 transition-colors">
                      Changed your mind? Approve anyway
                    </button>
                  )
                )}
                <ApproveAnywayDialog
                  open={showApproveAnyway}
                  onCancel={() => setShowApproveAnyway(false)}
                  onConfirm={() => { setShowApproveAnyway(false); setShowSurvey(true); }}
                />
              </div>
            ) : project.client_approved ? null : (
              <div className="space-y-2">
                <textarea rows={2} placeholder="Describe the changes you'd like..."
                  value={revisionNotes} onChange={(e) => setRevisionNotes(e.target.value)}
                  data-testid="revision-notes-input"
                  className="w-full bg-ug-surface border border-[#F97066]/30 text-zinc-100 rounded-md p-3 text-sm focus:border-[#F97066]/60 outline-none resize-none placeholder:text-ug-status-red/40" />
                {/* Either channel satisfies the request — a note here, or timestamped
                    comments on the video. The hint says which one is missing rather than
                    leaving a dead button with no explanation. */}
                {!canRequestRevisions ? (
                  <p className="font-sans text-[10px] text-slate-500 leading-relaxed" data-testid="revision-request-hint">
                    Add a note above, or leave at least one timestamped comment on the video,
                    so your editor knows what to change.
                  </p>
                ) : clientNoteCount > 0 && !revisionNotes.trim() ? (
                  <p className="font-sans text-[10px] text-slate-500 leading-relaxed" data-testid="revision-request-hint">
                    Sending {clientNoteCount} timestamped note{clientNoteCount !== 1 ? 's' : ''}.
                    Add a general note above if there is anything else.
                  </p>
                ) : null}
                <button onClick={async () => {
                  if (!canRequestRevisions || requestingRevisions) return;
                  setRequestingRevisions(true);
                  try {
                    await api.put(`/projects/${project.id}/request-revisions`, { notes: revisionNotes });
                    toast.success('Revisions requested — your editor has been notified');
                    setRevisionNotes('');
                    onRefresh();
                  } catch (e) {
                    // Surface the server's reason. It is the authority on this gate and its
                    // message names both ways to satisfy it; a bare "Failed" left the client
                    // with no idea what to do next.
                    toast.error(e?.response?.data?.detail || 'Could not request revisions. Please try again.');
                  } finally { setRequestingRevisions(false); }
                }} data-testid="request-revisions-btn"
                  disabled={!canRequestRevisions || requestingRevisions}
                  className="w-full py-2.5 rounded-md bg-velvet-burgundy/80 hover:bg-velvet-burgundy text-white text-[12px] font-medium tracking-wide transition-colors text-center disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-velvet-burgundy/80">
                  {requestingRevisions ? 'Sending...' : 'Request Revisions'}
                </button>
              </div>
            )
          )}

          {/* Client: Approve button + Review form + Deliverables */}
          {/* Gate: server already strips `versions` for clients pre-PM-approval, so
              versions.length > 0 ⇒ draft is approved and viewable. Don't depend on
              editor_status_id / admin_status_id — those are stripped from client responses. */}
          {user.role === 'client' && versions.length > 0 && !project.revisions_requested && (
            <div className="space-y-4 mt-2">
              {!project.client_approved ? (
                <>
                  <div className="text-center text-[10px] text-slate-600 font-mono">— or —</div>
                  {!showSurvey ? (
                    <button onClick={() => setShowSurvey(true)} data-testid="approve-project-btn"
                      className="w-full py-2.5 rounded-md bg-oxidized-teal/80 hover:bg-oxidized-teal text-white text-[12px] font-medium tracking-wide transition-colors text-center">
                      Approve Project
                    </button>
                  ) : (
                    <div className="p-4 rounded-xl bg-ug-raised border border-ug-border">
                      <SatisfactionSurvey
                        submitting={surveySubmitting}
                        onCancel={() => setShowSurvey(false)}
                        onSubmit={async (satisfaction) => {
                          setSurveySubmitting(true);
                          try {
                            await api.put(`/projects/${project.id}/client-approve`, { satisfaction });
                            toast.success('Project approved! Your files are now available.');
                            onRefresh();
                          } catch { toast.error('Failed'); }
                          finally { setSurveySubmitting(false); }
                        }}
                      />
                    </div>
                  )}
                </>
              ) : (
                <div className="space-y-4">
                  <div className="p-3 rounded-md bg-[#22C55E]/10 border border-[#22C55E]/30 text-center">
                    <span className="font-sans text-xs text-[#22C55E] font-medium">Project Approved</span>
                    <Check className="w-4 h-4 text-[#22C55E] inline ml-1" />
                  </div>

                  {/* Review form — only if not already reviewed */}
                  {!project.client_reviewed ? (
                    <div className="p-4 rounded-md bg-ug-surface/50 border border-ug-border space-y-3">
                      <h4 className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70">Rate Your Experience</h4>
                      <p className="text-[9px] text-slate-500 -mt-1 mb-2">Your star rating is for internal quality assessment only and will not be shared with your editor. Your written feedback below will be shared with the editor to help improve future edits.</p>
                      <div className="flex gap-1">
                        {[1,2,3,4,5].map(s => (
                          <button key={s} id={`star-${s}`} onClick={() => {
                            document.querySelectorAll('[id^="star-"]').forEach((el, i) => el.style.color = i < s ? '#d4af37' : '#334155');
                            document.getElementById('review-rating').value = s;
                          }} style={{ color: '#334155', fontSize: '24px', cursor: 'pointer', transition: 'color 0.15s' }}>&#9733;</button>
                        ))}
                        <input type="hidden" id="review-rating" defaultValue="0" />
                      </div>
                      <textarea id="review-feedback" rows={3} placeholder="How was your experience? Any notes for your editor for next time?"
                        className="w-full bg-ug-surface border border-ug-border text-zinc-100 rounded-md p-3 text-sm focus:border-gold-leaf/40 outline-none resize-none placeholder:text-white/40" />
                      <button onClick={async () => {
                        const rating = parseInt(document.getElementById('review-rating')?.value) || 0;
                        const feedback = document.getElementById('review-feedback')?.value || '';
                        if (!rating && !feedback) { toast.error('Please leave a rating or feedback'); return; }
                        try {
                          await api.post(`/projects/${project.id}/reviews`, { rating, feedback });
                          await api.put(`/projects/${project.id}`, { client_reviewed: true });
                          toast.success('Thank you for your feedback!'); onRefresh();
                        } catch { toast.error('Failed'); }
                      }} className="px-6 py-2 rounded-md bg-gold-leaf text-obsidian font-sans text-xs font-medium uppercase tracking-wider hover:bg-gold-leaf/90 transition-colors">
                        Submit Review
                      </button>
                    </div>
                  ) : (
                    <div className="p-3 rounded-md bg-ug-surface/50 border border-ug-border text-center">
                      <span className="font-sans text-[11px] font-medium text-slate-400">Thank you for your feedback</span>
                      <Check className="w-3.5 h-3.5 text-ug-gold inline ml-1" />
                    </div>
                  )}

                  {/* Deliverables — visible only after approval */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70">Your Deliverables</h4>
                    </div>
                    {removed ? (
                      <div className="p-3 rounded-lg bg-ug-status-red/[0.06] border border-ug-status-red/20" data-testid="deliverables-removed">
                        <p className="font-sans text-[11px] text-ug-status-red font-medium">{removed.state === 'gone' ? 'Your files have been removed.' : 'Your files have been removed, and can be brought back.'}</p>
                        <p className="font-sans text-[10px] text-zinc-500 mt-1">{removed.text}</p>
                      </div>
                    ) : (() => {
                      // The version's own films, then every version's project files and assets, once each (lib/finalFiles.js).
                      const vFiles = listed(versions, currentV);
                      if (vFiles.length === 0 && !project.photo_delivery_link) return <p className="text-[10px] text-slate-600 italic">Deliverables will appear here once uploaded by your editor.</p>;
                      return (
                        <div className="space-y-1.5">
                          {/* Download All, as it always was: everything in one archive. */}
                          {vFiles.length > 0 && (
                            <div className="mb-2">
                              {delivZipJob ? (
                                <ZipProgress job={delivZipJob} id="deliv" onRetry={() => { setDelivZipJob(null); downloadZip(''); }} />
                              ) : (
                                <button onClick={() => downloadZip('')} data-testid="download-all-deliverables-btn"
                                  className="w-full flex items-center justify-center gap-2 p-2.5 rounded-md bg-gold-leaf/10 border border-gold-leaf/25 hover:bg-gold-leaf/20 transition-colors">
                                  <Download className="w-4 h-4 text-ug-gold" />
                                  <span className="font-sans text-[11px] font-medium text-ug-gold uppercase tracking-wider">Download All as ZIP{totalBytes(vFiles) > 0 ? ` (${sizeWords(totalBytes(vFiles))})` : ''}</span>
                                </button>
                              )}
                            </div>
                          )}
                          {/* Then the two groups (lib/finalFiles.split): the films, each downloaded on its own straight
                              from storage with nothing to prepare, and the project files and assets, which get one small
                              zip of their own (Ryan, 2026-10-13). */}
                          {(() => {
                            const { films, rest, restZip } = split(vFiles);
                            const row = (f, i, film) => {
                              const Icon = f.type === 'review_video' ? Film : f.type === 'project_file' ? FileText : f.type === 'edit_xml' ? FileCode : Package;
                              const label = f.type === 'project_file' ? 'Project File' : f.type === 'edit_xml' ? 'Timeline XML' : 'Asset';
                              const handleDownload = async () => {
                                // Try presign-download first (cleaner — surfaces JSON 410 on missing keys)
                                if (f.key) {
                                  try {
                                    const r = await api.post('/storage/presign-download', { key: f.key });
                                    if (r.data?.download_url) { window.location.href = r.data.download_url; return; }
                                  } catch (err) {
                                    if (err.response?.status === 410) {
                                      toast.error(err.response.data?.message || 'This file is no longer available in storage. Please contact support.');
                                      return;
                                    }
                                    // Other errors → fall through to /storage/serve attempt
                                  }
                                }
                                if (f.file_id) {
                                  // HEAD-check via fetch so we can surface 410 cleanly instead of
                                  // opening a tab that lands on raw R2 NoSuchKey XML.
                                  try {
                                    const probe = await fetch(`${api.defaults.baseURL}/storage/serve/${f.file_id}`, { method: 'GET', credentials: 'include', redirect: 'manual' });
                                    if (probe.status === 410) {
                                      const data = await probe.json().catch(() => ({}));
                                      toast.error(data.message || 'This file is no longer available in storage. Please contact support.');
                                      return;
                                    }
                                  } catch {}
                                  window.open(`${api.defaults.baseURL}/storage/serve/${f.file_id}`, '_blank');
                                }
                              };
                              return (
                                <button key={`${f.file_id || f.key || f.name}-${i}`} onClick={handleDownload} data-testid={film ? 'deliverable-film-row' : 'deliverable-rest-row'}
                                  className="flex items-center gap-1.5 py-1.5 px-2 rounded bg-ug-surface/40 border border-ug-border-subtle hover:bg-ug-surface/60 transition-colors min-w-0 w-full text-left">
                                  <Icon className="w-3 h-3 text-oxidized-teal shrink-0" />
                                  <span className="text-[10px] text-zinc-100/80 flex-1 truncate min-w-0">{f.name}</span>
                                  {sizeWords(f.size) && <span className="text-[8px] text-zinc-500 font-mono shrink-0">{sizeWords(f.size)}</span>}
                                  {film
                                    ? <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-ug-gold shrink-0">Download</span>
                                    : <span className="text-[7px] text-zinc-500 font-mono shrink-0 px-1 py-0.5 rounded bg-white/[0.03]">{label}</span>}
                                  <Download className="w-3 h-3 text-ug-gold shrink-0" />
                                </button>
                              );
                            };
                            return (
                              <>
                                {films.length > 0 && (
                                  <div className="space-y-1.5" data-testid="deliverables-films">
                                    <p className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500 pt-1">{FILMS_TITLE} ({films.length})</p>
                                    <p className="text-[10px] text-slate-500" data-testid="deliverables-films-hint">{FILMS_HINT}</p>
                                    {films.map((f, i) => row(f, i, true))}
                                  </div>
                                )}
                                {rest.length > 0 && (
                                  <div className="space-y-1.5 pt-2" data-testid="deliverables-rest">
                                    <p className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500">{REST_TITLE} ({rest.length})</p>
                                    {restZip && (restZipJob ? (
                                      <ZipProgress job={restZipJob} id="rest" onRetry={() => { setRestZipJob(null); downloadZip('rest'); }} />
                                    ) : (
                                      <button onClick={() => downloadZip('rest')} data-testid="download-rest-zip-btn"
                                        className="w-full flex items-center justify-center gap-2 p-2 rounded-md border border-gold-leaf/25 hover:bg-gold-leaf/10 transition-colors">
                                        <Download className="w-3.5 h-3.5 text-ug-gold" />
                                        <span className="font-sans text-[10px] font-medium text-ug-gold uppercase tracking-wider">{REST_ZIP}{totalBytes(rest) > 0 ? ` (${sizeWords(totalBytes(rest))})` : ''}</span>
                                      </button>
                                    ))}
                                    {rest.map((f, i) => row(f, i, false))}
                                  </div>
                                )}
                              </>
                            );
                          })()}
                          {project.photo_delivery_link && (
                            <a href={project.photo_delivery_link} target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-2 p-2.5 rounded bg-oxidized-teal/[0.06] border border-oxidized-teal/20 hover:bg-oxidized-teal/10 transition-colors">
                              <Download className="w-4 h-4 text-oxidized-teal shrink-0" />
                              <span className="text-[12px] text-zinc-100/80 flex-1">Download All Photos</span>
                              <span className="text-[9px] text-oxidized-teal font-mono shrink-0">ZIP/Folder</span>
                            </a>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* First-draft attestation record — admin review surface (fee mechanic).
              Re-surfaced here, right before approve-and-send: N/A items prominent. */}
          {isAdmin && mhGate?.attestation && (
            <AttestationReview projectId={project.id} attestation={mhGate.attestation} onChanged={fetchMhGate} canFlag />
          )}

          {/* Part B: revision-note dispositions (N/A reasons + admin-bypass tombstones)
              for the cycle that produced this draft — right before approve-and-send. */}
          {/* Shown to the CLIENT as well (operator, 2026-08-14). Safe by
              construction rather than by care: the server strips
              `resolved_by_name` and `admin_upload_bypass` from a client's copy of
              these comments, so the editor is never named here and the internal
              "an admin shipped this over an unresolved note" tombstones — and
              their banner, which counts from the same field — cannot render for
              them. Nothing role-specific is filtered in this component, so if
              that strip is ever removed the client starts seeing both. */}
          {(isAdmin || isClient) && project.project_type !== 'photo' && (project.current_version || 0) >= 2 && (
            <RevisionDispositionReview projectId={project.id} cycleVersion={(project.current_version || 0) - 1}
              forClient={isClient} />
          )}

          {/* Admin: Approve & Send to Client */}
          {isAdmin && awaitingRelease(project) && (
            <button onClick={async () => {
              try { const r = await api.put(`/projects/${project.id}/approve-draft`); toast.success(r.data?.message || 'Approved — client can now see it'); onRefresh(); } catch { toast.error('Failed'); }
            }} data-testid="approve-draft-btn"
              className="w-full p-3 rounded-md bg-gold-leaf/[0.06] border border-gold-leaf/25 text-ug-gold font-sans text-[10px] font-semibold uppercase tracking-wider hover:bg-gold-leaf/10 transition-colors flex items-center justify-center gap-2">
              <Check className="w-4 h-4" /> Approve & Send to Client
            </button>
          )}
        </div>
      </div>
    );
  }

  // ─── FULLSCREEN MODE: Frame.io-style layout ───
  return (
    <div data-testid="review-tab" className="h-full flex flex-col">
      {attestModal}
      {notesGateModal}
      <div className="p-4 space-y-3 flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="flex items-center gap-2 flex-wrap pb-1 shrink-0">
        {compareMode ? (
          <>
            {cutSwitcher}
            {(isEditor || isAdmin) && !readOnly && (
              <Button size="sm" variant="outline" onClick={openUploadFlow} data-testid="upload-version-btn"
                className="tap-auto h-7 font-sans text-[10px] font-semibold border-gold-leaf/20 text-ug-gold/70 hover:bg-gold-leaf/10 ml-auto">
                <Upload className="w-3.5 h-3.5 mr-1" /> Upload V{(project.current_version || 0) + 1}
              </Button>
            )}
          </>
        ) : (
          <>
            <VersionPicker value={currentV} versions={versions} onChange={setSelectedVersion} testId="version-select" />
            {cutSwitcher}
            {versions.length >= 2 && (
              <Button size="sm" variant="outline" onClick={() => setCompareMode(true)}
                data-testid="compare-toggle"
                className="tap-auto h-7 font-sans text-[10px] font-semibold border-ug-border text-slate-400">
                <Columns className="w-3.5 h-3.5 mr-1" /> Compare
              </Button>
            )}
            {(isEditor || isAdmin) && !readOnly && (
              <Button size="sm" variant="outline" onClick={openUploadFlow} data-testid="upload-version-btn"
                className="tap-auto h-7 font-sans text-[10px] font-semibold border-gold-leaf/20 text-ug-gold/70 hover:bg-gold-leaf/10 ml-auto">
                <Upload className="w-3.5 h-3.5 mr-1" /> Upload V{(project.current_version || 0) + 1}
              </Button>
            )}
          </>
        )}
      </div>

      {/* Editor Upload Panel — drag-and-drop to R2 */}
      {showUpload && (isEditor || isAdmin) && renderUploadPanel()}

      {/* Frame.io-style: videos left, comments right */}
      <div className="flex-1 flex gap-3 min-h-0">
        {/* LEFT: Video area */}
        <div className="flex-1 min-w-0 flex flex-col">
          <SafariWarning />
          {/* Video Player(s) */}
          {compareMode ? (
            <div className="flex-1 flex flex-col min-h-0">
              <div className="flex items-center gap-2 mb-1 shrink-0">
                <VersionPicker value={compareV} versions={versions} onChange={setCompareVersion} testId="compare-version-select" />
                <Button size="sm" variant="default" onClick={() => setCompareMode(false)}
                  data-testid="compare-toggle"
                  className="tap-auto h-7 font-sans text-[10px] font-semibold bg-gold-leaf text-obsidian">
                  <Columns className="w-3.5 h-3.5 mr-1" /> Compare
                </Button>
                <div className="flex-1" />
                <VersionPicker value={currentV} versions={versions} onChange={setSelectedVersion} testId="version-select" />
              </div>
              <ComparePlayer
                leftUrl={getRawVideoUrl(compareV)} rightUrl={getRawVideoUrl(currentV)}
                leftComments={compareLeftComments} rightComments={cutComments}
                activePanel={activePanel} onSelectPanel={setActivePanel}
                onSeek={(t) => { setCurrentTime(t); setCompareTime(t); }}
                onTimeUpdate={handleTimeUpdate}
                seekRef={compareSeekRef}
              />
            </div>
          ) : (
            <>
              {/* Piece 1 (2026-06-10): additive transcode-status indicator. Reads
                  CF status via stream-info; raw playback continues underneath.
                  Shows NOTHING for state none/unknown/ready (e.g. the 269 legacy
                  empty-uid videos), "processing" only while genuinely processing,
                  and a DISTINCT message on error — never lies. */}
              {streamStatus && !streamInfo.ready && ['pendingupload','downloading','queued','inprogress'].includes(streamStatus.state) && (
                <div data-testid="stream-processing-indicator" className="shrink-0 mb-1.5 flex items-center gap-2 bg-ug-gold/10 border border-ug-gold/20 rounded-md px-3 py-1.5">
                  <Loader2 className="w-3.5 h-3.5 text-ug-gold animate-spin shrink-0" strokeWidth={2} />
                  <span className="font-sans text-[10px] text-ug-gold leading-tight">
                    Optimized version processing{typeof streamStatus.pct_complete === 'number' ? ` — ${Math.round(streamStatus.pct_complete)}%` : ''} — ready in a few minutes. Playing standard quality meanwhile.
                  </span>
                </div>
              )}
              {streamStatus && streamStatus.state === 'error' && (
                <div data-testid="stream-error-indicator" className="shrink-0 mb-1.5 flex items-center gap-2 bg-zinc-500/10 border border-zinc-500/20 rounded-md px-3 py-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 text-zinc-400 shrink-0" strokeWidth={2} />
                  <span className="font-sans text-[10px] text-zinc-400 leading-tight">Optimized version unavailable — playing standard quality.</span>
                </div>
              )}
              {/* Raw-playback marker, fullscreen twin — see the drawer copy for why. */}
              {user.role !== 'client' && streamInfo && streamInfo.state === 'none' && !streamInfo.ready && (
                <p data-testid="raw-playback-marker-fs" className="tap-auto font-sans text-[10px] text-zinc-500 mb-1.5 shrink-0 flex items-center gap-1.5">
                  <AlertTriangle className="w-3 h-3 shrink-0 text-zinc-500" />
                  No Stream copy — clients get the raw file (slow to load and scrub). Re-push it from Settings &rarr; Cloudflare Stream.
                </p>
              )}
              {removed ? (
                <div className="w-full aspect-video rounded-md overflow-hidden border border-ug-border"><FilesRemovedPanel notice={removed} /></div>
              ) : (
              <ReviewPlayer videoUrl={getVideoUrl(currentV)} hlsUrl={getHlsUrl(currentV)} comments={cutComments} currentTime={currentTime}
                onTimeUpdate={handleTimeUpdate} onSeek={handlePlayerSeek} onClickTimeline={handleTimelineClick} onPlayPause={leaveMarker} controlRef={mainPlayerRef}
                playing={playing} onTogglePlay={setPlaying} videoRef={mainVideoRef}
                originalPath={streamInfo.hls_url ? `/projects/${project.id}/original/${currentV}${activeCutId ? `?file_id=${activeCutId}` : ''}` : null}
                reviewFileId={activeCut ? activeCut.file_id : (versions.find(v => v.version_number === currentV)?.review_video_file_id || '')}
                reviewFileKey={activeCut ? (activeCut.key || '') : (versions.find(v => v.version_number === currentV)?.review_video_key || '')} />
              )}
              {streamStatus?.pruned && (
                <p data-testid="pruned-playback-note-fs" className="tap-auto font-sans text-[11px] text-zinc-500 mt-1.5 shrink-0">
                  {/* a .mov or HEVC master (pruned from 60 days after approval, any format) may not play in every browser */}
              {streamStatus.may_not_play ? 'If this film does not play here, download it to watch.' : 'Loading may take a moment for completed projects.'}
                </p>
              )}
            </>
          )}
        </div>

        {/* RIGHT: Comments panel */}
        <div className="w-72 xl:w-80 shrink-0 flex flex-col border-l border-ug-border pl-3">
          <div className="flex items-center justify-between mb-2 shrink-0">
            {/* The heading names the CUT, not just the version — the rail holds
                one part's notes and must say which part without being counted. */}
            <h4 className="font-sans text-[10px] font-semibold uppercase tracking-wider text-slate-400" data-testid="review-comments-heading">
              {compareMode ? `V${activePanel === 'left' ? compareV : currentV} Notes` : `V${currentV} Notes`}
              {!compareMode && companionCuts.length > 0 && (
                <span className="text-ug-gold"> · {cutLabelById[activeCutId] || 'This cut'}</span>
              )}
            </h4>
            <span className="font-sans text-[9px] font-semibold text-slate-600" data-testid="review-comments-count">
              {(() => {
                const n = (compareMode ? (activePanel === 'left' ? compareLeftComments : cutComments) : cutComments).length;
                return `${n} ${n === 1 ? 'comment' : 'comments'}`;
              })()}
            </span>
          </div>
          {compareMode && <p className="text-[9px] text-slate-600 mb-2">Click a video to switch notes</p>}

          {/* Comments list — the ACTIVE CUT's notes only. Other parts are other
              portions of the day; their notes live behind their own pill.
              In compare mode, both sides are scoped to the SELECTED cut. */}
          <div className="flex-1 overflow-y-auto space-y-1.5 min-h-0" data-testid="review-comments-list">
            {(compareMode ? (activePanel === 'left' ? compareLeftComments : cutComments) : cutComments).map(c => {
              const isOwnClient = user.role === 'client' && c.author_id === user.id;
              const isEditing = editingCommentId === c.id;
              if (c.deleted) {
                // Tombstone — only editors/admins receive deleted comments from the API.
                return (
                  <div key={c.id} className="flex gap-2 items-start p-2.5 rounded-md bg-white/[0.01] border border-dashed border-ug-border-subtle opacity-70" data-testid={`comment-tombstone-${c.id}`}>
                    <span className="font-sans text-[10px] font-semibold text-slate-600 bg-white/[0.03] px-1.5 py-0.5 rounded shrink-0 mt-0.5">{formatTime(c.timecode)}</span>
                    <div className="flex-1 min-w-0">
                      <span className="font-sans text-[8px] font-medium text-ug-status-red/80 uppercase block">Removed by {c.author_name}</span>
                      <p className="text-slate-500 text-[11px] line-through">{c.body}</p>
                    </div>
                    <Trash2 className="w-3 h-3 text-slate-600 shrink-0 mt-1" />
                  </div>
                );
              }
              return (
              <div key={c.id} className={`flex gap-2 items-start p-2.5 rounded-md ${c.resolved ? 'opacity-50 bg-white/[0.01]' : 'bg-ug-surface/50'} border border-ug-border-subtle`}>
                <button onClick={() => {
                  if (compareMode && compareSeekRef.current) { compareSeekRef.current(c.timecode); return; }
                  // CUTS: a timecode only means something on ITS cut's timeline.
                  // The rail is already scoped to the active cut, so this is a
                  // no-op in practice — kept as the guard that makes the seek
                  // below safe no matter what ends up in the list.
                  const noteCut = c.deliverable_id || '';
                  if (noteCut !== activeCutId) { setSelectedCutId(noteCut); setCurrentTime(c.timecode); return; }
                  // Move the VIDEO, not only the clock. Setting the clock alone left a paused player showing one
                  // frame under another time, and the next note took the time shown, not the frame.
                  if (mainPlayerRef.current) mainPlayerRef.current.seek(c.timecode);
                  else setCurrentTime(c.timecode);
                }}
                  title={`Jump to ${formatTime(c.timecode)}`} data-testid={`comment-time-btn-${c.id}`}
                  className="font-sans text-[10px] font-semibold text-ug-gold bg-gold-leaf/10 px-1.5 py-0.5 rounded shrink-0 mt-0.5 tap-sm hover:bg-gold-leaf/20">
                  {formatTime(c.timecode)}
                </button>
                <div className="flex-1 min-w-0">
                  <span className="font-sans text-[8px] font-medium text-slate-500 uppercase block">
                    {c.author_name}{c.edited && <span className="text-slate-600 normal-case" data-testid={`comment-edited-badge-${c.id}`}> · edited</span>}
                  </span>
                  {isEditing ? (
                    <div className="flex gap-1 mt-0.5">
                      <Input value={editingText} onChange={e => setEditingText(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleEditComment(c.id); if (e.key === 'Escape') { setEditingCommentId(null); setEditingText(''); } }}
                        autoFocus className="tap-auto bg-ug-surface border-ug-border text-zinc-100 h-7 text-[11px]" data-testid={`edit-comment-input-${c.id}`} />
                      <button onClick={() => handleEditComment(c.id)} className="tap-sm text-oxidized-teal hover:opacity-80" title="Save" data-testid={`save-comment-btn-${c.id}`}><Check className="w-3.5 h-3.5" /></button>
                      <button onClick={() => { setEditingCommentId(null); setEditingText(''); }} className="tap-sm text-slate-600 hover:text-zinc-300" title="Cancel" data-testid={`cancel-edit-comment-btn-${c.id}`}><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ) : (
                    <p className="text-zinc-100/70 text-[11px]">{c.body}</p>
                  )}
                  {/* Part B: N/A disposition + reason — admin/editor visibility only
                      (v1 scope; client-facing disposition display is a follow-up). */}
                  {/* The reason now reaches the CLIENT too — a bare "couldn't do"
                      with no explanation is worse than the green tick it replaced.
                      The editor is never named: `resolved_by_name` and
                      `resolved_by` are stripped server-side for clients
                      (server.py::get_review_comments), so there is no name here
                      to accidentally render. */}
                  {c.resolved && c.resolution_type === 'na' && c.na_reason && (
                    <p className="tap-auto text-[9px] text-amber-200/70 italic mt-0.5" data-testid={`comment-na-reason-${c.id}`}>
                      {(isEditor || isAdmin) ? 'Could not do' : "Couldn't be done"}: "{c.na_reason}"
                    </p>
                  )}
                  {/* Part B admin-bypass tombstone — an admin uploaded the new draft
                      while this note was unresolved. Never a silent gap. */}
                  {(isEditor || isAdmin) && c.admin_upload_bypass && !c.resolved && (
                    <p className="tap-auto text-[9px] text-velvet-burgundy mt-0.5" data-testid={`comment-bypass-tombstone-${c.id}`}>
                      Admin uploaded V{c.admin_upload_bypass.uploaded_version} while this note was unresolved — never addressed by the editor.
                    </p>
                  )}
                  {/* Plain-language dispositions (2026-07-26). "Done" is the primary,
                      labelled action; the can't-do valve is secondary and spelled out
                      in words. The bare "N/A" abbreviation was being read as "done" by
                      editors for whom English is a second language. Sits on its own row
                      inside the text column so the note body keeps the full rail width. */}
                  {!isEditing && !c.resolved && (isEditor || isAdmin) && naOpenId !== c.id && (
                    <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                      <button onClick={() => handleResolve(c.id)} title="I made this change"
                        data-testid={`comment-resolve-btn-${c.id}`}
                        className="tap-auto flex items-center gap-1 font-sans text-[9px] font-semibold uppercase tracking-wider text-oxidized-teal border border-oxidized-teal/50 hover:bg-oxidized-teal/10 rounded px-2 py-1 transition-colors">
                        <Check className="w-3 h-3" strokeWidth={2.5} />Done
                      </button>
                      <button onClick={() => { setNaOpenId(c.id); setNaReason(''); }}
                        title="I could not make this change. A reason is required."
                        data-testid={`comment-na-btn-${c.id}`}
                        className="tap-auto font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500 hover:text-amber-300 border border-ug-border hover:border-amber-500/50 rounded px-2 py-1 transition-colors">Can't do this</button>
                    </div>
                  )}
                  {naOpenId === c.id && !c.resolved && (
                    <div className="mt-1.5 space-y-1.5" data-testid={`comment-na-form-${c.id}`}>
                      <p className="tap-auto text-[9px] text-amber-200/80 leading-relaxed" data-testid={`comment-na-explainer-${c.id}`}>
                        Use this only when the change is not possible, for example the footage does not exist.
                        Your project manager will see this note and your reason. If you already made the change,
                        close this and press Done instead. There is no penalty for an honest answer.
                      </p>
                      <input value={naReason} onChange={e => setNaReason(e.target.value)} maxLength={200} autoFocus
                        onKeyDown={e => { if (e.key === 'Enter') handleResolveNa(c.id); if (e.key === 'Escape') { setNaOpenId(null); setNaReason(''); } }}
                        placeholder="Why is this not possible? (required)" data-testid={`comment-na-reason-input-${c.id}`}
                        className="tap-auto w-full bg-ug-surface border border-amber-500/30 text-zinc-100 text-[10px] rounded px-2 py-1.5 placeholder:text-white/35 focus:border-amber-500/60 outline-none" />
                      <div className="flex gap-1.5">
                        <button type="button" onClick={() => handleResolveNa(c.id)} disabled={naReason.trim().length < 5} data-testid={`comment-na-apply-${c.id}`}
                          className="tap-auto font-sans text-[8px] font-semibold uppercase tracking-wider text-amber-300 border border-amber-500/50 hover:bg-amber-500/10 rounded px-2 py-1 transition-colors disabled:opacity-40">
                          Send reason
                        </button>
                        <button type="button" onClick={() => { setNaOpenId(null); setNaReason(''); }} data-testid={`comment-na-cancel-${c.id}`}
                          className="tap-auto font-sans text-[8px] font-semibold uppercase tracking-wider text-slate-500 hover:text-zinc-300 rounded px-2 py-1 transition-colors">
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                {!isEditing && isOwnClient && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => { setEditingCommentId(c.id); setEditingText(c.body); }} className="tap-sm text-slate-600 hover:text-ug-gold" title="Edit comment" data-testid={`edit-comment-btn-${c.id}`}><Pencil className="w-3 h-3" /></button>
                    <button onClick={() => handleDeleteComment(c.id)} className="tap-sm text-slate-600 hover:text-ug-status-red" title="Delete comment" data-testid={`delete-comment-btn-${c.id}`}><Trash2 className="w-3 h-3" /></button>
                  </div>
                )}
                {/* A refused note is NOT a completed one. This used to show the
                    amber badge to admin/editor and fall through to a green tick
                    for everyone else — so a client saw the same ✓ on "we did it"
                    and on "we can't do this", which is the one reading that is
                    definitely wrong. Same state, same badge, for every role;
                    only the wording differs (internal shorthand vs plain English
                    for the person who asked for the change). */}
                {c.resolved && (c.resolution_type === 'na' ? (
                  <span className="tap-auto font-sans text-[8px] font-semibold uppercase tracking-wider text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5 shrink-0 mt-1 whitespace-nowrap" data-testid={`comment-na-badge-${c.id}`}>
                    {(isEditor || isAdmin) ? 'Not done' : "Couldn't do"}
                  </span>
                ) : (
                  <CheckCircle className="w-3 h-3 text-oxidized-teal shrink-0 mt-1" />
                ))}
              </div>
              );
            })}
            {(compareMode && activePanel === 'left' ? compareComments : comments).length === 0 && (
              <div className="text-center py-8">
                <MessageSquare className="w-8 h-8 text-slate-700 mx-auto mb-2" />
                <p className="text-[11px] text-slate-500">No comments yet</p>
              </div>
            )}
          </div>

          {/* Comment input */}
          {!readOnly && (!compareMode || activePanel === 'right') && (
            <div className="mt-2 shrink-0 border-t border-ug-border pt-2">
              {markerTime !== null && (
                <div className="flex items-center gap-1 mb-1">
                  <span className="font-sans text-[9px] font-semibold text-ug-gold bg-gold-leaf/10 px-1.5 py-0.5 rounded" data-testid="review-comment-marker">at {formatTime(markerTime)}</span>
                  <button onClick={() => setMarkerTime(null)} className="text-slate-600 tap-sm" data-testid="review-comment-marker-clear"><X className="w-3 h-3" /></button>
                </div>
              )}
              {/* Comment input — hidden from editors (they can see comments but not add) */}
              {user.role !== 'editor' && (
              <div className="flex gap-1.5">
                <Input value={newComment} onChange={e => setNewComment(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddComment()}
                  placeholder={markerTime !== null ? `Comment at ${formatTime(markerTime)}...` : 'Leave a comment...'}
                  className="tap-auto bg-ug-surface border-ug-border text-zinc-100 h-8 text-[11px]" data-testid="review-comment-input" />
                <Button onClick={handleAddComment} size="sm" className="bg-gold-leaf text-obsidian hover:bg-gold-leaf/90 h-8 w-8 p-0 shrink-0" data-testid="review-comment-send">
                  <Send className="w-3.5 h-3.5" />
                </Button>
              </div>
              )}
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );

  // renderUploadPanel helper (used by both drawer and fullscreen modes)
  function renderUploadPanel() {
    const isV1 = (project.current_version || 0) === 0;
    const assetsOk = assetFiles.length > 0 || (assetsUnchanged && !isV1);
    // CUTS: previous version's cut inventory for the V2+ carry-forward controls.
    const prevVer = versions.find(v => v.version_number === (project.current_version || 0));
    const primaryCarryOk = primaryUnchanged && !isV1 && !!prevVer?.review_video_file_id;
    const prevCompanions = !isV1 ? (prevVer?.files || []).filter(f => f.type === 'review_video' && f.file_id && f.file_id !== prevVer?.review_video_file_id) : [];
    // Bug A: gate the submit on ACTUAL upload success (count-based, self-healing —
    // re-adding a failed file restores the count). A failed background upload never
    // populates preUploaded, so the count stays short -> button disabled.
    // CUTS note: primary checked by its OWN key ('video') so an uploaded
    // companion can never mask a failed primary.
    const uploadedVals = Object.values(preUploaded);
    const reviewUploaded = primaryCarryOk || !!preUploaded['video']?.file_id;
    const cutsUploaded = extraCuts.every(c => !!preUploaded[c.key]?.file_id);
    const cutLabelsOk = extraCuts.every(c => (c.label || '').trim());
    // Labels are the cut identity, so once ANY cut is in play the main video
    // needs a user-typed label too (carried-forward primary keeps its stored one).
    const hasCuts = extraCuts.length > 0 || unchangedCutIds.length > 0;
    const primaryLabelOk = !hasCuts || primaryCarryOk || !!primaryLabelInput.trim();
    // MANDATORY DISPOSITION: every cut on the previous version must be carried
    // forward (checkbox), replaced by a new upload with the SAME label, or said
    // to be replaced by one of this draft's new films (the select): a cut can't
    // silently disappear between versions. (The backend enforces the identical
    // rule; lib/finalFiles.cutStatus is its mirror.)
    const freshNow = freshFilms({ mainFile: reviewVideoFile, mainUnchanged: primaryUnchanged, mainLabel: primaryLabelInput, extraCuts });
    const prevCutStatus = cutStatus(prevCompanions, extraCuts, unchangedCutIds, replacedBy, freshNow);
    const prevCutsOk = prevCutStatus.every(s => s.ok);
    const projUploaded = uploadedVals.filter(k => k.type === 'project_file').length >= projectFiles.length;
    const assetUploaded = assetFiles.length === 0 || uploadedVals.filter(k => k.type === 'asset').length >= assetFiles.length;
    const xmlRequired = user.role === 'editor';
    const xmlUploaded = !timelineXml || !!preUploaded['xml']?.file_id;
    const uploadsSettledOk = reviewUploaded && cutsUploaded && projUploaded && xmlUploaded && assetUploaded;
    // A selected file that finished (not in-flight) but never landed in preUploaded = a failed upload.
    const anyUploadFailed = !bgUploading && (
      (!!reviewVideoFile && !reviewUploaded) ||
      (extraCuts.length > 0 && !cutsUploaded) ||
      (projectFiles.length > 0 && !projUploaded) ||
      (!!timelineXml && !xmlUploaded) ||
      (assetFiles.length > 0 && !assetUploaded)
    );
    const asksDelivery = user.role === 'editor';
    const deliveryOk = !asksDelivery || deliveryComplete(delivery);
    const canSubmit = !uploading && (!!reviewVideoFile || primaryCarryOk) && cutLabelsOk && primaryLabelOk && prevCutsOk && projectFiles.length > 0 && (!xmlRequired || !!timelineXml) && assetsOk && uploadsSettledOk && deliveryOk;
    return (
      <div className="mt-3 p-4 rounded-md bg-ug-raised/50 border border-gold-leaf/15 space-y-3" data-testid="version-upload-panel">
        <h3 data-role="section-title" className="font-sans font-bold text-sm text-zinc-100 flex items-center gap-2">
          <Package className="w-4 h-4 text-ug-gold" /> Upload Version {(project.current_version || 0) + 1}
        </h3>
        <p className="text-[10px] text-slate-400">All {xmlRequired ? 'four' : 'three'} uploads are <span className="text-ug-gold font-medium">required</span> before you can submit.</p>
        <div>
          <div {...makeDropHandler(setReviewVideoFile)} data-testid="drop-review-video"
            className={`p-4 rounded-md border-2 border-dashed cursor-pointer text-center transition-all ${(reviewVideoFile || primaryCarryOk) ? 'border-oxidized-teal/30 bg-oxidized-teal/[0.02]' : 'border-white/[0.1] hover:border-gold-leaf/30'} ${primaryUnchanged ? 'opacity-40 pointer-events-none' : ''}`}
            onClick={() => { if (primaryUnchanged) return; const i = document.createElement('input'); i.type = 'file'; i.accept = 'video/*'; i.onchange = (e) => { const f = e.target.files[0]; if (f) { setReviewVideoFile(f); bgUploadFile(f, 'project_deliverable', 'video'); } }; i.click(); }}>
            <Film className="w-5 h-5 text-ug-gold/40 mx-auto mb-1" />
            <span className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70 block mb-1">Review Video <span className="text-ug-status-red">*</span></span>
            {reviewVideoFile ? <div className="flex items-center justify-center gap-2"><span className="text-[11px] text-zinc-100">{reviewVideoFile.name}</span><span className="text-[10px] text-slate-500">({(reviewVideoFile.size/1048576).toFixed(1)} MB)</span><button onClick={e=>{e.stopPropagation();abortUpload('video');setReviewVideoFile(null);}} className="text-velvet-burgundy tap-sm"><X className="w-3 h-3"/></button></div> : <span className="text-[11px] text-slate-500">Drop video here</span>}
            {renderUploadState('video')}
            {/* Labels are the cut identity — once any cut is in play, THIS video
                needs a user-typed label too, exactly like each cut's Label field.
                Rendered INSIDE the dropzone so it's unambiguous which video it
                names (operator feedback: floating between the two boxes read as
                a third, unexplained thing). stopPropagation shields the input
                from the dropzone's click-to-browse. */}
            {hasCuts && !primaryCarryOk && (
              <div className="flex items-center justify-center gap-2 mt-3 pt-2.5 border-t border-oxidized-teal/15"
                onClick={e => e.stopPropagation()} data-testid="primary-cut-label-row">
                <span className="text-[10px] text-slate-400 shrink-0">Label <span className="text-zinc-200 font-medium">this main video</span>, like the cuts below:</span>
                <input value={primaryLabelInput} maxLength={40} placeholder="Label (e.g. Horizontal)"
                  onChange={e => setPrimaryLabelInput(e.target.value)}
                  data-testid="primary-cut-label-input"
                  className={`tap-auto w-44 bg-ug-surface border text-zinc-100 text-[10px] rounded px-2 py-1.5 placeholder:text-white/35 outline-none cursor-text ${primaryLabelInput.trim() ? 'border-ug-border focus:border-gold-leaf/40' : 'border-amber-500/40 focus:border-amber-500/70'}`} />
              </div>
            )}
          </div>
          {!isV1 && !!prevVer?.review_video_file_id && (
            <label className="flex items-center gap-2 mt-2 cursor-pointer px-1">
              <Checkbox checked={primaryUnchanged} data-testid="primary-unchanged-checkbox"
                onCheckedChange={v => { setPrimaryUnchanged(v); if (v && reviewVideoFile) { abortUpload('video'); setReviewVideoFile(null); } }} />
              <span className="text-[10px] text-slate-400">Review video unchanged since V{project.current_version}</span>
            </label>
          )}
        </div>
        {/* CUTS: companion review videos — extra exports of the SAME edit. */}
        <div className="p-4 rounded-md border border-ug-border bg-white/[0.01]" data-testid="extra-cuts-section">
          <div className="flex items-center gap-1.5 mb-1">
            <span className="font-sans text-[10px] font-semibold uppercase tracking-wider text-slate-400">Additional Cuts</span>
            <span className="text-[10px] text-slate-600">(optional — most projects don't need this)</span>
            <button type="button" onClick={() => setCutsHelpOpen(o => !o)} data-testid="cuts-help-btn"
              className="tap-auto text-slate-500 hover:text-ug-gold transition-colors" title="What is this?">
              <HelpCircle className="w-3.5 h-3.5" />
            </button>
          </div>
          {cutsHelpOpen && (
            <div className="mb-3 p-3 rounded-md bg-ug-gold/[0.05] border border-ug-gold/20" data-testid="cuts-help-panel">
              <p className="text-[11px] text-zinc-100/85 leading-relaxed">
                A <span className="text-ug-gold font-medium">cut</span> is a <span className="text-ug-gold font-medium">second file of the SAME edit</span>, delivered alongside the main video. It is rare — most projects only ever use the main Review Video box above.
              </p>
              <p className="text-[10px] text-slate-400 leading-relaxed mt-1.5">
                Use it only when the client ordered the same edit as separate files, for example:
              </p>
              <ul className="text-[10px] text-slate-400 leading-relaxed mt-1 list-disc pl-4 space-y-0.5">
                <li>a <span className="text-zinc-200">vertical 9:16 copy</span> of the highlight for social media, next to the horizontal one</li>
                <li>the <span className="text-zinc-200">Ceremony and Reception delivered as two separate files</span> instead of one long file</li>
              </ul>
              <p className="text-[10px] text-amber-300/90 leading-relaxed mt-1.5 font-medium">
                Do NOT use this for a revised version of the same video — that is a new Version (the upload you are already doing). If you fixed notes and re-exported, that is V{(project.current_version || 0) + 1}, not a cut.
              </p>
            </div>
          )}
          <div className="space-y-2.5 pb-1">
            {extraCuts.map((c, i) => (
              <div key={c.key} className="flex items-center gap-2" data-testid={`extra-cut-row-${i}`}>
                <input value={c.label} maxLength={40} placeholder="Label (e.g. Vertical)"
                  onChange={e => setExtraCuts(prev => prev.map(x => x.key === c.key ? { ...x, label: e.target.value } : x))}
                  data-testid={`cut-label-input-${i}`}
                  className={`tap-auto w-32 bg-ug-surface border text-zinc-100 text-[10px] rounded px-2 py-1.5 placeholder:text-white/35 outline-none shrink-0 ${(c.label || '').trim() ? 'border-ug-border focus:border-gold-leaf/40' : 'border-amber-500/40 focus:border-amber-500/70'}`} />
                <span className="text-[11px] text-zinc-100 truncate flex-1 min-w-0">{c.file.name}</span>
                <span className="text-[10px] text-slate-500 shrink-0">({(c.file.size/1048576).toFixed(1)} MB)</span>
                <button onClick={() => { abortUpload(c.key); setExtraCuts(prev => prev.filter(x => x.key !== c.key)); }}
                  data-testid={`remove-cut-btn-${i}`} className="text-velvet-burgundy tap-sm shrink-0"><X className="w-3 h-3"/></button>
                {renderUploadState(c.key)}
              </div>
            ))}
            <button type="button" data-testid="add-cut-btn"
              onClick={() => { const i = document.createElement('input'); i.type = 'file'; i.accept = 'video/*'; i.onchange = (e) => { const f = e.target.files[0]; if (f) { const key = `cut_${Date.now()}`; setExtraCuts(prev => [...prev, { key, file: f, label: '' }]); bgUploadFile(f, 'project_deliverable', key); } }; i.click(); }}
              className="tap-auto flex items-center gap-1 font-sans text-[9px] font-semibold uppercase tracking-wider text-ug-gold/70 border border-gold-leaf/20 hover:bg-gold-leaf/10 rounded px-2.5 py-1.5 transition-colors">
              <Plus className="w-3 h-3" /> Add a cut
            </button>
          </div>
          {!isV1 && prevCompanions.length > 0 && (
            <div className="space-y-1.5 mt-3 pt-3 border-t border-ug-border-subtle" data-testid="prev-cuts-disposition">
              <p className="text-[9px] font-sans font-semibold uppercase tracking-wider text-slate-500">V{project.current_version} had extra cuts. For each one: is it unchanged, or does a new file replace it?</p>
              {prevCutStatus.map(({ pc, ok, unchanged, sameLabel, by }) => (
                <div key={pc.file_id} className="flex items-center flex-wrap gap-x-2 gap-y-1 px-1" data-testid={`prev-cut-row-${pc.file_id}`}>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={unchanged} disabled={sameLabel || !!by} data-testid={`carry-cut-checkbox-${pc.file_id}`}
                      onCheckedChange={v => setUnchangedCutIds(prev => v ? [...prev, pc.file_id] : prev.filter(id => id !== pc.file_id))} />
                    <span className="text-[10px] text-slate-400">"{(pc.label || '').trim() || pc.name || 'Cut'}" unchanged since V{project.current_version}</span>
                  </label>
                  {!sameLabel && !unchanged && (
                    <label className="flex items-center gap-1.5">
                      <span className="text-[10px] text-slate-500">or replaced by</span>
                      {/* A re-export under a new name takes the earlier film's place: the earlier one is then not
                          carried into this version (it stays on V{n}, where it was). */}
                      <select value={by} disabled={freshNow.length === 0} data-testid={`replace-cut-select-${pc.file_id}`}
                        onChange={e => setReplacedBy(prev => ({ ...prev, [pc.file_id]: e.target.value }))}
                        className="tap-auto bg-ug-surface border border-ug-border text-zinc-100 text-[10px] rounded px-1.5 py-1 max-w-[220px] outline-none focus:border-gold-leaf/40">
                        <option value="">{freshNow.length === 0 ? 'add the new file above first' : 'choose the new file'}</option>
                        {freshNow.map(n => <option key={n.key} value={n.key}>{n.label}</option>)}
                      </select>
                    </label>
                  )}
                  {sameLabel && <span className="text-[9px] text-oxidized-teal" data-testid={`cut-replaced-hint-${pc.file_id}`}>new file added above ✓</span>}
                  {!!by && <span className="text-[9px] text-oxidized-teal" data-testid={`cut-replaced-by-hint-${pc.file_id}`}>it will not be carried into V{(project.current_version || 0) + 1} ✓</span>}
                  {!ok && <span className="text-[9px] text-amber-400" data-testid={`cut-undisposed-hint-${pc.file_id}`}>{CUT_HINT}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
        {/* A file the portal would not take (lib/roughEdit.js), said where the editor is looking: above the box it left. */}
        {refused.length > 0 && (
          <div className="p-2.5 rounded-lg bg-ug-status-red/[0.06] border border-ug-status-red/20" data-testid="upload-refused-summary">
            <p className="font-sans text-[10px] text-ug-status-red font-medium mb-1">Not accepted</p>
            {refused.map((f, i) => <p key={i} className="font-sans text-[11px] leading-snug text-zinc-300 mb-1" data-testid="upload-refused-reason">{f}</p>)}
            <button onClick={() => setRefused([])} data-testid="upload-refused-dismiss" className="tap-auto font-sans text-[9px] text-ug-gold underline mt-1">Dismiss</button>
          </div>
        )}
        <div {...makeDropHandler(setProjectFiles, true)} data-testid="drop-project-files"
          className={`p-4 rounded-md border-2 border-dashed cursor-pointer text-center transition-all ${projectFiles.length > 0 ? 'border-oxidized-teal/30 bg-oxidized-teal/[0.02]' : 'border-white/[0.1] hover:border-gold-leaf/30'}`}
          onClick={() => { const i = document.createElement('input'); i.type = 'file'; i.multiple = true; i.onchange = (e) => { const files = Array.from(e.target.files); setProjectFiles(prev => [...prev, ...files]); files.forEach((f, idx) => bgUploadFile(f, 'project_deliverable', `project_${Date.now()}_${idx}`)); }; i.click(); }}>
          <FileText className="w-5 h-5 text-ug-gold/40 mx-auto mb-1" />
          <span className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70 block mb-1">Project File <span className="text-ug-status-red">*</span></span>
          {projectFiles.length > 0 ? projectFiles.map((f,i)=><div key={i} className="flex items-center justify-center gap-2"><span className="text-[11px] text-zinc-100">{f.name}</span><span className="text-[10px] text-slate-500">({(f.size/1048576).toFixed(1)} MB)</span><button onClick={e=>{e.stopPropagation();setProjectFiles(prev=>prev.filter((_,idx)=>idx!==i));}} className="text-velvet-burgundy tap-sm"><X className="w-3 h-3"/></button></div>) : <span className="text-[11px] text-slate-500">Drop project file here (Premiere, Resolve, FCP timeline)</span>}
          {renderUploadState('project')}
        </div>
        {(() => {
          // THE TIMELINE XML (lib/timelineXml.js): one file, its own slot; required of an editor
          const takeXml = (f) => {
            if (!f) return;
            if (!isTimelineXmlName(f.name)) { toast.error('The timeline XML is an .xml, .fcpxml or .zip file'); return; }
            setTimelineXml(f); bgUploadFile(f, 'project_deliverable', 'xml');
          };
          const dropXml = {
            onDragOver: (e) => { e.preventDefault(); e.currentTarget.classList.add('border-gold-leaf/50', 'bg-gold-leaf/[0.04]'); },
            onDragLeave: (e) => { e.currentTarget.classList.remove('border-gold-leaf/50', 'bg-gold-leaf/[0.04]'); },
            onDrop: (e) => { e.preventDefault(); e.currentTarget.classList.remove('border-gold-leaf/50', 'bg-gold-leaf/[0.04]'); takeXml(Array.from(e.dataTransfer.files)[0]); },
          };
          return (
            <div {...dropXml} data-testid="drop-timeline-xml"
              className={`p-4 rounded-md border-2 border-dashed cursor-pointer text-center transition-all ${timelineXml ? 'border-oxidized-teal/30 bg-oxidized-teal/[0.02]' : 'border-white/[0.1] hover:border-gold-leaf/30'}`}
              onClick={() => { const i = document.createElement('input'); i.type = 'file'; i.accept = XML_ACCEPT; i.onchange = (e) => takeXml(e.target.files[0]); i.click(); }}>
              <FileCode className="w-5 h-5 text-ug-gold/40 mx-auto mb-1" />
              <span className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70 block mb-1">
                Timeline XML {xmlRequired ? <span className="text-ug-status-red">*</span> : <span className="text-slate-500">(optional for staff)</span>}
              </span>
              {timelineXml ? (
                <div className="flex items-center justify-center gap-2">
                  <span className="text-[11px] text-zinc-100" data-testid="timeline-xml-name">{timelineXml.name}</span>
                  <button onClick={e => { e.stopPropagation(); setTimelineXml(null); setPreUploaded(prev => { const n = { ...prev }; delete n.xml; return n; }); setBoxProgress(prev => { const n = { ...prev }; delete n.xml; return n; }); }}
                    data-testid="timeline-xml-remove" className="text-velvet-burgundy tap-sm"><X className="w-3 h-3" /></button>
                </div>
              ) : (
                <div className="space-y-0.5" data-testid="timeline-xml-how">
                  <span className="text-[11px] text-slate-500 block">Export the finished timeline as XML and drop it here</span>
                  {XML_HOW.map(([app, how]) => (
                    <span key={app} className="text-[9px] text-slate-500 block"><span className="text-slate-400 font-medium">{app}:</span> {how}</span>
                  ))}
                </div>
              )}
              {renderUploadState('xml')}
            </div>
          );
        })()}
        <div>
          <div {...makeDropHandler(setAssetFiles, true)} data-testid="drop-asset-files"
            className={`p-4 rounded-md border-2 border-dashed cursor-pointer text-center transition-all ${assetsOk ? 'border-oxidized-teal/30 bg-oxidized-teal/[0.02]' : 'border-white/[0.1] hover:border-gold-leaf/30'} ${assetsUnchanged ? 'opacity-40 pointer-events-none' : ''}`}
            onClick={() => { if (assetsUnchanged) return; const i = document.createElement('input'); i.type = 'file'; i.multiple = true; i.onchange = (e) => { const files = Array.from(e.target.files); setAssetFiles(prev => [...prev, ...files]); files.forEach((f, idx) => bgUploadFile(f, 'project_asset', `asset_${Date.now()}_${idx}`)); }; i.click(); }}>
            <Package className="w-5 h-5 text-ug-gold/40 mx-auto mb-1" />
            <span className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70 block mb-1">Assets {isV1 ? <span className="text-ug-status-red">*</span> : <span className="text-slate-500">(or check below)</span>}</span>
            {assetFiles.length > 0 ? assetFiles.map((f,i)=><div key={i} className="flex items-center justify-center gap-2"><span className="text-[11px] text-zinc-100">{f.name}</span><span className="text-[10px] text-slate-500">({(f.size/1048576).toFixed(1)} MB)</span><button onClick={e=>{e.stopPropagation();setAssetFiles(prev=>prev.filter((_,idx)=>idx!==i));}} className="text-velvet-burgundy tap-sm"><X className="w-3 h-3"/></button></div>) : <span className="text-[11px] text-slate-500">Drop assets here (LUTs, fonts, graphics, exports)</span>}
            {renderUploadState('asset')}
          </div>
          {!isV1 && (
            <label className="flex items-center gap-2 mt-2 cursor-pointer px-1">
              <Checkbox checked={assetsUnchanged} onCheckedChange={v => { setAssetsUnchanged(v); if (v) setAssetFiles([]); }} />
              <span className="text-[10px] text-slate-400">Assets unchanged since V{project.current_version}</span>
            </label>
          )}
        </div>
        {/* Footage Report — embedded in upload panel for V1 only */}
        {(project.current_version || 0) === 0 && (
          <div ref={footageFormRef}>
            <FootageReportForm projectId={project.id} projectType={project.project_type} existingReport={footageReport} onSubmitted={() => {
              api.get(`/projects/${project.id}/footage-report`).then(r => setFootageReport(r.data?.report || null)).catch(() => {});
            }} />
          </div>
        )}
        {asksDelivery && <DeliveryQuestions value={delivery} onChange={setDelivery} />}
        {anyUploadFailed && (
          <div className="p-2.5 rounded-lg bg-ug-status-red/[0.06] border border-ug-status-red/20 mb-2" data-testid="upload-incomplete-banner">
            <p className="font-sans text-[10px] text-ug-status-red font-medium">One or more files didn't finish uploading.</p>
            <p className="font-sans text-[9px] text-zinc-500 mt-0.5">Remove and re-add the failed file(s), then submit. (Your draft will not be submitted until all uploads succeed.)</p>
          </div>
        )}
        {uploadFailures.length > 0 && (
          <div className="p-2.5 rounded-lg bg-ug-status-red/[0.06] border border-ug-status-red/20 mb-2" data-testid="upload-failure-summary">
            <p className="font-sans text-[10px] text-ug-status-red font-medium mb-1">Upload failed</p>
            {uploadFailures.map((f, i) => <p key={i} className="font-sans text-[9px] text-zinc-500">{f}</p>)}
            <button onClick={() => setUploadFailures([])} className="font-sans text-[9px] text-ug-gold underline mt-1">Dismiss</button>
          </div>
        )}
        <div className="flex gap-2 items-center flex-wrap">
          {/* Silent-disable fix: requirement check row — every submit condition
              visible at a glance (display-only; gate/upload logic untouched). */}
          <div data-testid="upload-req-row" className="tap-auto w-full flex items-center gap-3 flex-wrap px-1">
            {[
              ['req-video', 'Review video', !!reviewVideoFile || primaryCarryOk],
              ...(extraCuts.length > 0 || (hasCuts && !primaryCarryOk) ? [['req-cut-labels', 'Cut labels', cutLabelsOk && primaryLabelOk]] : []),
              ...(prevCompanions.length > 0 ? [['req-prev-cuts', `V${project.current_version} cuts`, prevCutsOk]] : []),
              ['req-project', 'Project file', projectFiles.length > 0],
              ...(xmlRequired ? [['req-xml', 'Timeline XML', !!timelineXml]] : []),
              ['req-assets', 'Assets', assetsOk],
              ...(isV1 ? [['req-footage', 'Footage analysis', !!footageReport]] : []),
              ...(asksDelivery ? [['req-delivery', 'This delivery', deliveryOk]] : []),
            ].map(([tid, label, done]) => (
              <span key={tid} data-testid={tid} data-done={done ? '1' : '0'}
                className={`tap-auto inline-flex items-center gap-1 font-sans text-[9px] font-medium ${done ? 'text-oxidized-teal' : 'text-slate-500'}`}>
                {done ? <Check className="w-2.5 h-2.5 shrink-0" strokeWidth={3} /> : <Circle className="w-2 h-2 shrink-0" />} {label}
              </span>
            ))}
          </div>
          <Button onClick={handleUploadVersion} disabled={!canSubmit || bgUploading || uploading || ((project.current_version || 0) === 0 && !footageReport)} data-testid="submit-version-btn"
            className="bg-gold-leaf text-obsidian hover:bg-gold-leaf/90 font-sans text-[10px] font-semibold h-9 disabled:opacity-30">
            <Plus className="w-3.5 h-3.5 mr-1" /> {uploading ? 'Uploading...' : bgUploading ? 'Wait for uploads to finish...' : `Create V${(project.current_version || 0) + 1}`}
          </Button>
          <Button variant="outline" onClick={() => { setShowUpload(false); setReviewVideoFile(null); setProjectFiles([]); setTimelineXml(null); setAssetFiles([]); setAssetsUnchanged(false); setExtraCuts([]); setPrimaryUnchanged(false); setUnchangedCutIds([]); setPrimaryLabelInput(''); setDelivery(EMPTY_DELIVERY); setRefused([]); }}
            className="border-ug-border text-slate-400 font-sans text-[10px] font-semibold h-9">Cancel</Button>
          {(() => {
            /* Silent-disable fix: NAME the unmet requirement(s). Footage-analysis-only
               → clickable jump-to-form anchor (display-only; submit logic untouched). */
            if (uploading || bgUploading) return null;
            const missing = [
              (!reviewVideoFile && !primaryCarryOk) && 'Review Video',
              extraCuts.length > 0 && !cutLabelsOk && 'Cut Labels',
              hasCuts && !primaryCarryOk && !primaryLabelInput.trim() && 'Main Video Label',
              !prevCutsOk && `V${project.current_version} Cuts (unchanged, or replaced by a new file)`,
              projectFiles.length === 0 && 'Project File',
              xmlRequired && !timelineXml && 'Timeline XML',
              !assetsOk && 'Assets',
              isV1 && !footageReport && 'Footage Analysis',
              !deliveryOk && 'Delivery Questions',
            ].filter(Boolean);
            if (missing.length === 0) return null;
            if (missing.length === 1 && missing[0] === 'Footage Analysis') {
              return (
                <button type="button" data-testid="submit-missing-hint"
                  onClick={() => footageFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                  className="tap-auto font-sans text-[10px] font-medium text-amber-400 underline decoration-amber-400/40 underline-offset-2 text-left">
                  Complete Footage Analysis to submit
                </button>
              );
            }
            return (
              <span data-testid="submit-missing-hint" className="tap-auto font-sans text-[10px] font-medium text-amber-400">
                Missing: {missing.join(' · ')}
              </span>
            );
          })()}
        </div>
      </div>
    );
  }
}
